/**
 * PALLET RACKING (DPL) 3D Product Configurator — Multi-unidad
 * Clonado de Media Carga (configurator-media-carga.js), que comparte con esta gama el
 * ensamblaje kit-based: un único GLB (pallet-racking.glb) con piezas nombradas que se
 * clonan y se posicionan analíticamente.
 *
 * 8 ejes: Altura · Anchura de módulo · Niveles · Nº de módulos del pack ·
 *         Balda de malla · Protector de puntal · Carga por palé · Tipo de módulo.
 * Fondo (110 cm) y color son FIJOS: se pintan como dato, no son selectores.
 *
 * Colores reales del producto (la gama es BITONO):
 *   Bastidores → galvanizado · Largueros → NARANJA RAL 2004
 *   Malla → galvanizada · Protector de puntal → AMARILLO RAL 1023
 *
 * El GLB no lleva color ni texturas: lo pinta applyColorToUnit por nombre de material.
 */

(function () {

  // Estado global
  var scene, camera, renderer, controls, loader;
  var _raycaster = null, _ptrDownX = 0, _ptrDownY = 0;
  var _cameraUserMoved = false;
  // Solo indica que el kit GLB se está descargando; sirve para el spinner y para el timeout
  // de seguridad de loadAllUnits().
  //
  // ⚠️ Los selectores de configuración NO se guardan con esto, y es a propósito. Al clonar la
  // gama se puso `if (isLoadingModel) return;` en los seis, y eso DESCARTA el clic del usuario:
  // en una conexión lenta la descarga del kit tarda segundos y durante ese rato los botones
  // "no hacen nada" — quedan con el foco puesto pero el título, el precio, las cotas y el visor
  // siguen en la configuración aterrizada. Es el fallo que reportaron los usuarios ("no cambia
  // los atributos según lo que ha seleccionado", con el visor vacío).
  //
  // Quitarlo es seguro: el precio y los textos no dependen del 3D, y la geometría se pone al
  // día sola. `reloadActiveUnit()` con el kit sin cargar llama a `loadAllUnits()`, y `loadKit()`
  // deduplica por `kitPending`, así que cuando el .glb llega se reensambla desde la config
  // ACTUAL, no desde la que hubiera al pulsar. Una vez cargado el kit, el reensamblado es
  // síncrono dentro del mismo tick, así que esta bandera nunca llega a verse en un clic.
  var isLoadingModel = false;

  // ─── Mapping / price cache ────────────────────────────────────────────────────
  // Los precios se obtienen de la API de producto (/products/{handle}.json).
  // El handle se construye determinísticamente desde la config + tipo de módulo
  // (inicial/adicional). Verificado contra los 626 productos reales de la gama.
  var priceCache = {};  // key "unitType|alto|ancho|fondo|niveles|tipo" → {price, compareAt, variantId, available, images}
  var priceInflight = {};  // misma key + allowSearch → [callbacks] mientras la petición está en curso

  // RECORTE ESTRUCTURAL altura↔niveles, verificado contra los 616 productos reales.
  // No es una limitación de catálogo: es que a esas alturas solo existen esos niveles.
  // Es determinista, así que el grisado NO necesita red para resolverlo.
  var ALTURA_NIVELES = { '300': [2], '350': [2, 3], '400': [2, 3], '500': [3], '600': [3, 4] };

  // Nº de palés por hueco según el ancho de módulo (verificado):
  //   nº palés = (niveles + 1) × palés_por_hueco × nº_módulos   ← el SUELO también almacena
  var PALES_POR_HUECO = { '185': 2, '270': 3 };

  function nivelesValidos(alto) {
    return ALTURA_NIVELES[String(alto)] || [];
  }

  // HUECOS DEL CATÁLOGO — los 24 SKU que no existen, y por qué hacen falta aquí.
  //
  // El grisado por red es FAIL-OPEN a propósito: solo bloquea si el `.js` RESPONDE
  // `available:false`. Un producto que NO EXISTE no responde nada, así que por esa vía no se
  // grisa NUNCA y el usuario acaba en una combinación sin precio. Estos huecos son
  // deterministas, así que se resuelven con tabla, igual que el recorte altura↔niveles.
  //
  // Medido sobre los 616 del catálogo: los 8 grupos de **carga 500 kg con pack de 1 módulo**
  // (familias 3 y 4 × los 4 acabados) tienen 13 de los 16 combos dimensionales. Faltan
  // siempre los tres mismos. Con pack de 2-4 módulos están los 16.
  var PAL_HUECOS_500_PACK1 = { '400|185|2': 1, '400|270|2': 1, '600|185|3': 1 };
  function combinacionExiste(cfg) {
    var mods = parseInt(cfg.modulos, 10) || 1;
    if (String(cfg.carga) !== '500' || mods !== 1) return true;
    var k = cfg.alto + '|' + cfg.ancho + '|' + (parseInt(cfg.niveles, 10) || 0);
    return !PAL_HUECOS_500_PACK1[k];
  }
  // Primer nivel de `validos` con el que la config EXISTE en el catálogo (o el primero si
  // ninguno existe, para conservar el comportamiento anterior y que el grisado lo marque).
  function nivelExistente(cfg, validos) {
    for (var i = 0; i < validos.length; i++) {
      if (combinacionExiste(Object.assign({}, cfg, { niveles: validos[i] }))) return validos[i];
    }
    return validos[0];
  }
  // Capacidad en palés. El "+1" no es un error: el SUELO del hueco también es plano de
  // almacenaje, así que con 2 niveles de largueros caben 3 alturas de palé.
  function palesTotales(cfg) {
    var niv = parseInt(cfg.niveles, 10) || 0;
    var mod = parseInt(cfg.modulos, 10) || 1;
    var porHueco = PALES_POR_HUECO[String(cfg.ancho)] || 2;
    return (niv + 1) * porHueco * mod;
  }
  // Carga por palé. Manda SIEMPRE el eje de la config, porque es un SELECTOR: lo que el
  // usuario elige es lo que se está configurando.
  //
  // ⚠️ Antes se miraba primero `data.loadingCapacity`, y estaba mal: ese metacampo describe
  // el producto ATERRIZADO y es un valor fijo de la página, así que ganaba siempre y el panel
  // seguía diciendo "1000 kg/palé" aunque el usuario pulsara 500. El metacampo solo sirve como
  // respaldo si la config no trae un valor válido.
  //
  // NUNCA se usa `custom.capacidad_de_carga_por_balda_kg`: solo está en 64 de los 616 y es
  // incoherente (da el mismo 3000 para ancho 185 que para 270).
  function cargaPorPale(cfg) {
    var c = parseInt(cfg && cfg.carga, 10);
    if (c === 500 || c === 1000) return c;
    var data = window.PalConfiguratorData;
    var m = data && parseInt(data.loadingCapacity, 10);
    return (m === 500 || m === 1000) ? m : 1000;
  }

  var units = [
    { id: 1, tipoModulo: 'inicial',
      config: { alto: '300', ancho: '185', fondo: '110', niveles: 2, modulos: 1,
                malla: false, protector: false, carga: '1000' },
      model: null, xOffset: 0 }
  ];
  var activeUnitIdx = 0;
  var forceAdditionalView = false; // true cuando se entra desde la ficha de un "módulo adicional" (default del tipo de la unidad 0)
  var bundleQty = 1;               // nº de CONJUNTOS (el run completo replicado en pasillos paralelos en Z)
  var visualClonesEnabled = false; // false = bundleQty solo multiplica el carrito; true = clones 3D visibles
  var bundleClones = [];           // grupos THREE de las copias b≥1 (solo visuales)
  var AISLE_GAP_M = 0.7;           // separación entre pasillos (m)
  var MAX_UNITS = 50;
  var MAX_BUNDLES = 20;

  // Historial undo/redo
  var configHistory = [];
  var historyPointer = -1;

  var dimLinesGroup = null;
  var dimLabelEls = {};

  // ─── Colores reales del producto (RAL) ───────────────────────────────────────
  // El GLB llega SIN color (todos los materiales en gris neutro) y sin ninguna textura,
  // así que estos valores son los que de verdad se ven.
  var PAL_COLORS = {
    galva:     0xC9CDD2,  // galvanizado — bastidores y malla
    naranja:   0xE25303,  // NARANJA RAL 2004 (Reinorange) — largueros
    protector: 0xF3C300   // AMARILLO RAL 1023 — protectores de puntal
  };
  var METAL_PROPS     = { roughness: 0.65, metalness: 0.18 };
  var RAL_PROPS       = { roughness: 0.60, metalness: 0.10 };  // pintura en polvo
  var PROTECTOR_PROPS = { roughness: 0.65, metalness: 0.05 };

  var BALDA_LABELS = { no: 'Sin balda', si: 'Malla galvanizada' };

  // ─── i18n helper ─────────────────────────────────────────────────────────────
  var I18N_DEFAULTS = {
    hint_rotate:'Arrastra para rotar', hint_zoom:'Scroll para zoom',
    error_title:'No se pudo cargar el modelo', error_text:'Comprueba tu conexión e inténtalo de nuevo.',
    retry:'Reintentar', cam_perspective:'Vista perspectiva', cam_front:'Vista frontal',
    cam_side:'Vista lateral', cam_top:'Vista superior', cam_undo:'Deshacer', cam_lbl_front:'Frontal', cam_lbl_side:'Lateral', cam_lbl_top:'Superior', cam_lbl_undo:'Deshacer', cam_lbl_redo:'Rehacer', cam_lbl_reset:'Reiniciar', cam_lbl_fit:'Todo', cam_lbl_dims:'Cotas', cam_lbl_clone:'Duplicar',
    cam_redo:'Rehacer', cam_reset:'Restablecer configuración',
    screenshot:'Descargar imagen', share:'Compartir config',
    url_copied:'URL copiada al portapapeles', unit_label:'Unidad',
    add_unit:'+ Añadir módulo', remove_unit:'Eliminar unidad',
    price_calc:'Calculando…', price_no_map:'Configura el mapeo de productos',
    price_label:'Precio', price_total:'Total', price_selected:'Configuración seleccionada',
    add_to_cart:'Añadir al carrito', adding:'Añadiendo...', added:'✓ Añadido',
    cart_error:'Error — Inténtalo de nuevo', no_stock:'Sin stock',
    val_malla_no:'Sin balda', val_malla_si:'Malla galvanizada',
    val_si:'Sí', val_no:'No',
    summary_prefix:'Estantería para palés',
    niveles_word:'niveles',
    // Ejes propios de esta gama
    opt_modulos:'Nº de módulos', opt_protector:'Protector de puntal',
    opt_carga:'Carga por palé', opt_malla:'Balda de malla',
    spec_pales:'Palés', spec_carga_pale:'Carga / palé',
    pdf_modulos:'Nº de módulos', pdf_protector:'Protector de puntal',
    pdf_carga_pale:'Carga / palé', pdf_pales:'Capacidad en palés',
    bundle_word:'conjuntos', bundle_subtotal:'Subtotal por conjunto',
    vat_incl:'IVA {rate}% incluido', vat_excl:'IVA excluido',
    files_subtitle:'Documentación técnica para descargar',
    tab_assembly:'Instrucciones de montaje', tab_datasheet:'Ficha técnica',
    pdf_save:'Guardar PDF', pdf_active_config:'Configuración activa',
    pdf_altura:'Altura', pdf_anchura:'Anchura', pdf_fondo:'Fondo', pdf_profundidad:'Profundidad',
    pdf_niveles:'Niveles', pdf_tipo_balda:'Tipo de balda', pdf_color:'Color', pdf_carga:'Carga / balda',
    pdf_dim_alto:'Alto total', pdf_dim_ancho_total:'Ancho total', pdf_dim_fondo:'Fondo',
    pdf_units_title:'Resumen de módulos', pdf_col_module:'Módulo', pdf_col_prof:'Prof.',
    pdf_count_one:'módulo', pdf_count_many:'módulos',
    pdf_buy_title:'¿Quieres pedirla?',
    pdf_buy_text:'Añade esta configuración exacta al carrito con un solo clic, o copia el enlace para revisarla más tarde.',
    pdf_buy_now:'Comprar ahora', pdf_view_config:'Ver configuración',
    pdf_mod_inicial:'Inicial', pdf_mod_adicional:'Adicional',
    adicional_invalid:'Un módulo adicional no puede ser más alto que el módulo a su izquierda.',
    adicional_un_modulo:'El módulo adicional solo está disponible de uno en uno.'
  };
  function t(key) {
    var i18n = window.PalConfiguratorData && window.PalConfiguratorData.i18n;
    if (i18n && i18n[key] !== undefined && i18n[key] !== '') return i18n[key];
    return I18N_DEFAULTS[key] !== undefined ? I18N_DEFAULTS[key] : key;
  }
  // Eje de balda: booleano (lleva malla o no), a diferencia de Media Carga, que tiene dos
  // materiales de balda. Aquí "sin balda" es una opción real del catálogo.
  function baldaDisplay(malla) {
    var k = malla ? 'si' : 'no';
    return t('val_malla_' + k) || BALDA_LABELS[k];
  }
  function siNo(v) { return v ? (t('val_si') || 'Sí') : (t('val_no') || 'No'); }
  // Infijo de ACABADO del SKU: '' liso · M malla · P protector · MP ambos.
  function palAcabado(cfg) {
    return (cfg.malla ? 'M' : '') + (cfg.protector ? 'P' : '');
  }
  // Unidad "kg/balda" localizada: deriva el sustantivo de balda de la etiqueta ya
  // traducida de capacidad (pdf_carga = "Last / Boden", "Load / shelf", "Nosnost /
  // police"…) → "kg/Boden". Sin claves de locale nuevas.
  function perShelfUnit() {
    var cap = String(t('pdf_carga') || 'Carga / balda');
    var shelf = cap.indexOf('/') > -1 ? cap.split('/').pop().trim() : 'balda';
    return 'kg/' + (shelf || 'balda');
  }

  // Cotas NOMINALES del producto (las que se enseñan al cliente).
  // El 185/270 del nombre comercial es la LUZ LIBRE entre puntales, no el paso entre ejes.
  var PRODUCT_DIMS = {
    frameHeights: { '300': 3000, '350': 3500, '400': 4000, '500': 5000, '600': 6000 },
    widths:  { '185': 1850, '270': 2700 },
    depths:  { '110': 1100 }
  };
  var FONDO_FIJO = '110';   // toda la gama; no es selector

  // ─── Constantes del kit de PALLET (MEDIDAS sobre el GLB, en metros) ──────────
  // El kit va horneado: cada nodo tiene scale 1, rotación identidad y su base en Y=0,
  // así que aquí no hay que compensar transformaciones (a diferencia de otras gamas).
  //
  // Paso entre EJES de bastidor. Es la cota que separa dos bastidores contiguos:
  //   luz libre (1,849 / 2,701) + ancho de perfil (0,080) = paso
  var PAL_PITCH_X = { '185': 1.92936, '270': 2.78133 };
  var PAL_KIT_LEFT_X = 0;               // el kit ya viene con el eje del puntal en X=0
  var PAL_BASTIDOR_WIDTH = 0.11898;     // ancho en X del bastidor (lo marca la placa base)

  // Altura real de los bastidores en el GLB (medida, no nominal)
  var PAL_FRAME_HEIGHTS_M = { '300': 3.00498, '350': 3.50128, '400': 3.9997, '500': 4.99857, '600': 5.99841 };

  // ⚠️ Cara superior del PERFIL del larguero, sobre la cota del nivel. NO es el alto de su
  // bbox (0,23493): ese lo marca el CONECTOR de los extremos, que sobresale 47,96 mm por
  // arriba. La malla se coloca justo en esta cota y queda ENRASADA con el canto.
  var LARGUERO_TOP = 0.18697;

  // Rejilla de ranurado del puntal: los niveles solo pueden caer en múltiplos de esto.
  var PASO_RANURA = 0.050;
  // Alto del larguero con su conector: hay que reservarlo por encima del último nivel para
  // que la pieza quepa dentro del bastidor.
  var LARGUERO_BBOX_H = 0.23493;

  function activeUnit() { return units[activeUnitIdx]; }
  function currentModel() { return activeUnit().model; }

  // ─── Kit GLB ──────────────────────────────────────────────────────────────────
  var kitMaster = null;    // escena maestra del kit (nunca se añade a scene)
  var kitNodes  = {};      // name (sin puntos) → Object3D del kit
  var kitLoaded = false;
  var kitPending = [];     // callbacks esperando a que cargue el kit

  function loadKit(url, cb) {
    if (kitLoaded) { cb(null); return; }
    kitPending.push(cb);
    if (kitPending.length > 1) return; // ya hay una carga en curso
    loader.load(url, function (gltf) {
      kitMaster = gltf.scene;
      kitMaster.traverse(function (obj) {
        if (obj.name) kitNodes[obj.name] = obj;
      });
      kitLoaded = true;
      var cbs = kitPending.slice(); kitPending = [];
      cbs.forEach(function (fn) { fn(null); });
    }, undefined, function (err) {
      var cbs = kitPending.slice(); kitPending = [];
      cbs.forEach(function (fn) { fn(err); });
    });
  }

  function cloneKitPiece(name) {
    // Three.js 0.134 elimina los puntos de los nombres de nodo GLTF
    var key = name.replace(/\./g, '');
    var src = kitNodes[key];
    if (!src) { console.warn('[MC] Pieza no encontrada en kit:', name, '(key:', key, ')'); return null; }
    var clone = src.clone(true);
    clone.traverse(function (child) {
      if (child.isMesh && child.material) {
        child.material = Array.isArray(child.material)
          ? child.material.map(function (m) { return m.clone(); })
          : child.material.clone();
      }
    });
    return clone;
  }

  function disposeModel(group) {
    if (!group) return;
    group.traverse(function (child) {
      if (!child.isMesh) return;
      // Geometría compartida con el kit: NO destruir. Solo los materiales clonados.
      var mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(function (m) { if (m) m.dispose(); });
    });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────
  function init() {
    var data = window.PalConfiguratorData;
    if (!data || !(data.glbUrl || data.kitUrl)) {
      console.error('[PAL] falta PalConfiguratorData.glbUrl (el kit 3D).');
      return;
    }

    var canvas = document.getElementById('configurator-canvas');
    if (!canvas) return;
    var viewerEl = canvas.parentElement;

    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.shadowMap.enabled = false;
    renderer.setSize(canvas.clientWidth || viewerEl.clientWidth, canvas.clientHeight || viewerEl.clientHeight, false);

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xFFFFFF);

    var _initW = canvas.clientWidth || viewerEl.clientWidth;
    var _initH = canvas.clientHeight || viewerEl.clientHeight;
    camera = new THREE.PerspectiveCamera(38, _initW / _initH, 0.01, 300);
    camera.position.set(2.8, 2.4, 3.8);

    scene.add(new THREE.AmbientLight(0xFFFFFF, 0.85));

    var keyLight = new THREE.DirectionalLight(0xFFFFFF, 1.0);
    keyLight.position.set(3, 8, 4);
    keyLight.castShadow = false;
    scene.add(keyLight);

    var fillLight = new THREE.DirectionalLight(0xF0F5FF, 0.40);
    fillLight.position.set(-3, 3, -2);
    scene.add(fillLight);

    var groundLight = new THREE.DirectionalLight(0xFFFFFF, 0.15);
    groundLight.position.set(0, -3, 0);
    scene.add(groundLight);

    var rimLight = new THREE.DirectionalLight(0xEEF0FF, 0.45);
    rimLight.position.set(-2, 3, -3);
    scene.add(rimLight);

    controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 1.0;
    controls.maxDistance = 60;
    controls.maxPolarAngle = Math.PI / 2;
    controls.target.set(0, 1.0, 0);
    controls.addEventListener('start', function () { _cameraUserMoved = true; });
    controls.update();

    if (THREE.Cache) THREE.Cache.enabled = true;

    addFloor();
    loader = new THREE.GLTFLoader();
    initDimLabels(viewerEl);
    addCameraButtons(viewerEl);

    // 1. Intentar restaurar config desde URL (share link)
    var restoredFromUrl = readUrlConfig();
    // 2. Si no hay URL params, pre-configurar desde el handle/SKU del producto actual
    if (!restoredFromUrl) { applyProductConfig(); primeLandingPrice(); }
    syncPanelToUnit();
    renderUnitTabs();
    bindSelectors();

    // Re-pintar el precio cuando se pulsa el toggle de IVA (app master-custom).
    // La app guarda el modo en localStorage; re-leemos y re-renderizamos tras el click.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('#mc-vat-toggle, .mc-vat-toggle__btn')) {
        setTimeout(function () { updatePriceAndCart(); }, 80);
      }
    });

    var cartBtn = document.getElementById('add-to-cart-btn');
    if (cartBtn) cartBtn.addEventListener('click', addAllToCart);

    var retryBtn = document.getElementById('model-retry');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        var errEl = document.getElementById('model-error');
        if (errEl) errEl.classList.add('hidden');
        loadAllUnits();
      });
    }

    addActionButtons();
    initStickyMobileCta();
    initStickyViewerShrink();
    initTechCard();
    initGallery();
    initConsultationButton();

    window.addEventListener('resize', onResize);
    var hint = document.getElementById('viewer-hint');
    if (hint) setTimeout(function () { hint.classList.add('hidden'); }, 4000);

    _raycaster = new THREE.Raycaster();
    renderer.domElement.addEventListener('pointerdown', function (e) {
      _ptrDownX = e.clientX; _ptrDownY = e.clientY;
    });
    renderer.domElement.addEventListener('pointerup', function (e) {
      if (units.length <= 1) return;
      var dx = e.clientX - _ptrDownX, dy = e.clientY - _ptrDownY;
      if (dx * dx + dy * dy > 64) return;
      var rect = renderer.domElement.getBoundingClientRect();
      var mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      _raycaster.setFromCamera(mouse, camera);
      for (var j = 0; j < units.length; j++) {
        if (!units[j].model || j === activeUnitIdx) continue;
        if (_raycaster.intersectObject(units[j].model, true).length) { setActiveUnit(j); break; }
      }
    });
    renderer.domElement.addEventListener('mousemove', function (e) {
      if (units.length <= 1 || !_raycaster) return;
      var rect = renderer.domElement.getBoundingClientRect();
      var mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      _raycaster.setFromCamera(mouse, camera);
      var hovering = false;
      for (var j = 0; j < units.length; j++) {
        if (!units[j].model || j === activeUnitIdx) continue;
        if (_raycaster.intersectObject(units[j].model, true).length) { hovering = true; break; }
      }
      renderer.domElement.style.cursor = hovering ? 'pointer' : '';
    });

    loadAllUnits();
    animate();

    // Hook de debug solo en desarrollo local. Es la vía con la que se verifican las
    // barridas de combinaciones por introspección de escena, sin depender de capturas.
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      window.__RC = {
        get scene() { return scene; }, get camera() { return camera; },
        get controls() { return controls; }, get units() { return units; },
        get renderer() { return renderer; }, get kitNodes() { return kitNodes; },
        derivePalSku: derivePalSku,
        configFromPalSku: configFromPalSku,
        levelY: levelY,
        nivelesValidos: nivelesValidos,
        palesTotales: palesTotales,
        THREE: THREE
      };
    }
  }

  function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
    updateDimLabels();
    updateActiveUnitArrow();
  }

  function onResize() {
    if (!renderer || !camera) return;
    var canvas = document.getElementById('configurator-canvas');
    if (!canvas) return;
    var viewerEl = canvas.parentElement;
    requestAnimationFrame(function () {
      var w = canvas.clientWidth || viewerEl.offsetWidth;
      var h = canvas.clientHeight || viewerEl.offsetHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    });
  }

  // ─── Resolución config → producto ───────────────────────────────────────────
  // ⚠️ AQUÍ ESTA GAMA SE APARTA DE LAS OTRAS CINCO, y no es una preferencia de estilo:
  //
  //  1. NO se puede construir el handle desde la config. En las demás gamas el handle
  //     español es determinista y siempre existe en la raíz de la tienda; aquí el mercado
  //     ES está en DRAFT, no hay hreflang="es" y el handle ES en raíz hace 302 a /de-de/.
  //     Por eso `buildPalHandle` no existe y `viaHreflang()` NO se porta.
  //  2. La vía autoritativa es el SKU, que no se traduce.
  //  3. Como respaldo hay `custom.option_groups_data`: un metacampo poblado en los 618
  //     productos que publica, por opción de cada eje, el handle destino y sus
  //     `handleTranslations` (cs, de, hu, pl, sk). Es una tabla de resolución ya traducida.

  function mappingKey(cfg, unitType) {
    return (unitType || 'inicial') + '|' + cfg.alto + '|' + cfg.ancho + '|' + cfg.fondo +
           '|' + cfg.niveles + '|' + cfg.modulos + '|' + cfg.carga + '|' + (palAcabado(cfg) || '-');
  }

  function shopRoot() {
    try { var r = window.Shopify && Shopify.routes && Shopify.routes.root; return (r && typeof r === 'string') ? r : '/'; }
    catch (e) { return '/'; }
  }
  // Idioma del mercado actual, para elegir en `handleTranslations`.
  function currentLocale() {
    try {
      var l = (document.documentElement.lang || '').toLowerCase().split('-')[0];
      if (l) return l;
    } catch (e) {}
    var r = shopRoot().replace(/^\//, '').split('-')[0];
    return r || 'de';
  }

  // ─── SKU determinista de la gama ────────────────────────────────────────────
  //   DPL[M|P|MP]{familia}{pack2}-{niveles}/{alto}{ancho}{fondo}ZG
  //   acabado: (nada) liso · M malla · P protector · MP ambos
  //   familia: 1 inicial 1000kg · 2 adicional 1000kg · 3 inicial 500kg · 4 adicional 500kg
  //   pack:    nº de módulos, a 2 cifras (01..04)
  // El SKU NO se traduce → identifica el producto equivalente en cualquier mercado, y es
  // la vía de la que se parte tanto para resolver como para leer la config de inicio.
  function derivePalSku(cfg, unitType) {
    var niv = parseInt(cfg.niveles, 10);
    var mod = parseInt(cfg.modulos, 10);
    if (!niv || !mod) return null;
    var adic = (unitType === 'adicional') ? 1 : 0;
    var familia = (String(cfg.carga) === '1000' ? 1 : 3) + adic;
    function p3(n) { n = String(parseInt(n, 10) || 0); return ('000' + n).slice(-3); }
    function p2(n) { n = String(parseInt(n, 10) || 0); return ('00' + n).slice(-2); }
    return 'DPL' + palAcabado(cfg) + familia + p2(mod) + '-' + niv + '/' +
           p3(cfg.alto) + p3(cfg.ancho) + p3(cfg.fondo) + 'ZG';
  }

  // Inverso de derivePalSku: los 8 ejes EXACTOS desde el SKU, sin depender del idioma.
  function configFromPalSku(sku) {
    var m = /^DPL(M|P|MP)?([1-4])(\d\d)-(\d)\/(\d{3})(\d{3})(\d{3})([A-Z]+)$/.exec(String(sku || '').trim());
    if (!m) return null;
    var acabado = m[1] || '';
    var familia = parseInt(m[2], 10);
    return {
      tipoModulo: (familia === 2 || familia === 4) ? 'adicional' : 'inicial',
      malla:      acabado.indexOf('M') > -1,
      protector:  acabado.indexOf('P') > -1,
      carga:      (familia === 1 || familia === 2) ? '1000' : '500',
      modulos:    parseInt(m[3], 10),
      niveles:    parseInt(m[4], 10),
      alto:       String(parseInt(m[5], 10)),
      ancho:      String(parseInt(m[6], 10)),
      fondo:      String(parseInt(m[7], 10))
    };
  }

  // ─── `custom.option_groups_data`: qué es y qué NO es ────────────────────────
  // Forma real (verificada volcando el metacampo de un producto, no supuesta):
  //
  //   { isMaster: false,
  //     subgroups: [ { title: "Altura (cm)", titleTranslations: {cs,de,hu,pl,sk},
  //                    type: "text",
  //                    options: [ { label: "300", active: true,
  //                                 handle: "...", handleTranslations: {cs,de,hu,pl,sk} }, … ] }, … ] }
  //
  // ⚠️ NO es una tabla de búsqueda por configuración: es navegación de UN EJE A LA VEZ
  // desde el producto en el que estás. Cada opción dice a qué producto irías si cambiases
  // SOLO ese eje, y `active` marca el valor vigente. Por eso:
  //   · sirve para el PRIMER cambio desde el producto aterrizado (handle ya traducido,
  //     cero peticiones), y para saber qué opciones existen;
  //   · NO sirve para una config arbitraria de 8 ejes → ahí manda la búsqueda por SKU.
  // Además el metacampo describe el producto ATERRIZADO: en cuanto el usuario se mueve,
  // deja de aplicar (el `.js` de un producto no trae metacampos).
  //
  // Y un regalo: `handle: null` materializa el recorte estructural. En un 300 cm de 2
  // niveles, las alturas 500/600 y los niveles 3/4 vienen SIN handle porque esa
  // combinación no existe. Es la misma verdad que la tabla ALTURA_NIVELES, publicada por
  // el propio catálogo.

  // Los títulos del metacampo vienen SIEMPRE en español (las traducciones van aparte, en
  // titleTranslations, y son solo para mostrar), así que se casa por el español.
  var OG_EJES = [
    { eje: 'alto',      re: /^altura/i,           valor: function (c) { return String(c.alto); } },
    { eje: 'ancho',     re: /^ancho/i,            valor: function (c) { return String(c.ancho); } },
    { eje: 'niveles',   re: /niveles/i,           valor: function (c) { return String(c.niveles); } },
    { eje: 'modulos',   re: /n[uú]mero de m[oó]dulos/i, valor: function (c) { return String(c.modulos); } },
    { eje: 'malla',     re: /balda/i,             valor: function (c) { return c.malla ? 'Malla galvanizada' : 'Sin balda'; } },
    { eje: 'protector', re: /protector/i,         valor: function (c) { return c.protector ? 'Sí' : 'No'; } },
    { eje: 'carga',     re: /carga por paleta/i,  valor: function (c) { return String(c.carga); } }
  ];

  function ogSubgrupos() {
    var data = window.PalConfiguratorData;
    var og = data && data.optionGroups;
    var subs = og && og.subgroups;
    return (subs && subs.length) ? subs : null;
  }
  function ogSubgrupoDe(eje) {
    var subs = ogSubgrupos();
    if (!subs) return null;
    var def = OG_EJES.filter(function (d) { return d.eje === eje; })[0];
    if (!def) return null;
    for (var i = 0; i < subs.length; i++) {
      if (def.re.test(String(subs[i].title || ''))) return subs[i];
    }
    return null;
  }
  // Etiqueta activa de un eje según el metacampo (la del producto aterrizado).
  function ogActivo(eje) {
    var g = ogSubgrupoDe(eje);
    if (!g) return null;
    for (var i = 0; i < (g.options || []).length; i++) {
      if (g.options[i].active) return String(g.options[i].label);
    }
    return null;
  }
  // Etiquetas de un eje que SÍ existen (tienen handle). null = no se sabe.
  function ogEtiquetasValidas(eje) {
    var g = ogSubgrupoDe(eje);
    if (!g) return null;
    return (g.options || []).filter(function (o) { return !!o.handle; })
                            .map(function (o) { return String(o.label); });
  }
  // Handle (ya traducido al mercado) para cambiar UN eje a un valor dado.
  function ogHandle(eje, etiqueta) {
    var g = ogSubgrupoDe(eje);
    if (!g) return null;
    var loc = currentLocale();
    for (var i = 0; i < (g.options || []).length; i++) {
      var o = g.options[i];
      if (String(o.label) !== String(etiqueta) || !o.handle) continue;
      var tr = o.handleTranslations || {};
      var h = tr[loc] || o.handle;
      return String(h).replace(/.*\/products\//, '').trim();
    }
    return null;
  }

  // Config del producto ATERRIZADO, congelada al arrancar: es la única contra la que el
  // metacampo puede resolver un salto de un eje.
  var cfgAterrizada = null;

  // Atajo de resolución: si `cfg` difiere de la config aterrizada en EXACTAMENTE un eje,
  // el metacampo ya publica el handle destino traducido. Si difiere en 0 o en ≥2, null.
  function handleFromOptionGroups(cfg, unitType) {
    if (!cfgAterrizada || !ogSubgrupos()) return null;
    if ((unitType || 'inicial') !== (cfgAterrizada._unitType || 'inicial')) return null;
    var distintos = [];
    for (var i = 0; i < OG_EJES.length; i++) {
      var d = OG_EJES[i];
      if (d.valor(cfg) !== d.valor(cfgAterrizada)) distintos.push(d.eje);
    }
    if (distintos.length !== 1) return null;
    var eje = distintos[0];
    var def = OG_EJES.filter(function (x) { return x.eje === eje; })[0];
    return ogHandle(eje, def.valor(cfg));
  }

  // ─── Composición del handle destino (saltos de VARIOS ejes) ───────────────────
  // `handleFromOptionGroups` solo cubre saltos de UN eje, que es lo único que el metacampo
  // publica. En cuanto el usuario mueve dos ejes (p. ej. altura + tipo de balda) hay que
  // componer, y aquí NO vale el camino de las otras cinco gamas —construir el handle español
  // y seguir su hreflang—: el mercado ES está en DRAFT y ese handle da 404 (medido en el
  // mercado DE contra un producto que sí existe).
  //
  // Los handles salen de una plantilla, así que sus tokens se separan en dos clases:
  //
  //   · alto / ancho / niveles → son NÚMEROS en posiciones deducibles: el token de
  //     dimensiones es inconfundible (\d+x\d+x\d+) y el de niveles es el siguiente. No hay
  //     que aprender nada, y MENOS MAL: son justo los dos ejes con huecos (`handle:null`
  //     por el recorte altura↔niveles), y de un hueco no se puede aprender ningún token.
  //
  //   · módulos / balda / protector / carga → llevan palabras TRADUCIDAS que no se pueden
  //     deducir (mit-stahlgitterboden, mit-rammschutz, feld/felder, regaly-paletowe…). Se
  //     APRENDEN del metacampo, que publica el handle ya traducido del salto de ese eje.
  //     Esos cuatro ejes nunca traen `handle:null`, así que el token siempre está.
  //
  // Devuelve un CANDIDATO, nunca una certeza: quien manda sigue siendo la comprobación de
  // SKU contra el .js. Si la plantilla no es la esperada se devuelve null y se cae a la
  // búsqueda de siempre, así que un cambio de nomenclatura del catálogo degrada, no rompe.
  var OG_EJES_APRENDIDOS = ['modulos', 'malla', 'protector', 'carga'];

  // Diferencia a nivel de token entre dos handles: prefijo y sufijo comunes, y lo de en medio.
  function ogDiffTokens(a, b) {
    var i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    var j = 0;
    while (j < (a.length - i) && j < (b.length - i) && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
    return { start: i, del: a.length - j - i, ins: b.slice(i, b.length - j) };
  }

  // Localiza en un handle los tokens de DIMENSIONES y el de NIVELES, casándolos contra los
  // valores del producto aterrizado (no contra una posición fija). El catálogo usa al menos
  // dos formatos —"300x185x110" en un token y "300-185-110" en tres—, así que se prueban los
  // dos y se exige que los números coincidan con la config aterrizada: si no coinciden, no
  // es lo que creemos y se abandona. Devuelve {iDim, nDim, iNiv} o null.
  function ogLocalizaDims(toks, base) {
    var A = String(base.alto), W = String(base.ancho), D = String(base.fondo), N = String(base.niveles);
    for (var i = 0; i < toks.length; i++) {
      if (toks[i] === A + 'x' + W + 'x' + D && String(toks[i + 1]) === N) return { iDim: i, nDim: 1, iNiv: i + 1 };
      if (toks[i] === A && toks[i + 1] === W && toks[i + 2] === D && String(toks[i + 3]) === N) {
        return { iDim: i, nDim: 3, iNiv: i + 3 };
      }
    }
    return null;
  }

  function palComposeHandle(cfg, unitType) {
    var data = window.PalConfiguratorData;
    var H0 = data && data.productHandle;
    if (!H0 || !cfgAterrizada || !ogSubgrupos()) return null;
    if ((unitType || 'inicial') !== (cfgAterrizada._unitType || 'inicial')) return null;

    function defDe(eje) {
      for (var k = 0; k < OG_EJES.length; k++) if (OG_EJES[k].eje === eje) return OG_EJES[k];
      return null;
    }
    function difiere(eje) {
      var d = defDe(eje);
      return d && d.valor(cfg) !== d.valor(cfgAterrizada);
    }

    // ── Elección de la BASE ────────────────────────────────────────────────────
    // Si cambia el acabado se parte del handle de ESE salto, no del aterrizado, porque en
    // algunos mercados el acabado cambia la PLANTILLA ENTERA del handle. Medido en PL:
    //   sin balda  → regaly-paletowe-300x185x110-2-polek-1-modul
    //   con malla  → palettenregale-grundregal-300-185-110-2-1000-1-wire-mesh
    // Partiendo del primero, el handle compuesto no sirve ni como query. Partiendo del
    // segundo, basta sustituir los números. En DE los dos comparten plantilla y da igual.
    var base = String(H0), ejeBase = null;
    var candidatos = ['malla', 'protector'];
    for (var b = 0; b < candidatos.length; b++) {
      if (!difiere(candidatos[b])) continue;
      var hb = ogHandle(candidatos[b], defDe(candidatos[b]).valor(cfg));
      if (!hb) continue;
      base = String(hb); ejeBase = candidatos[b];
      break;
    }
    var toks = base.split('-');
    var pos = ogLocalizaDims(toks, cfgAterrizada);
    if (!pos) return null;

    // ── Ediciones aprendidas del metacampo ─────────────────────────────────────
    // Solo se pueden aplicar si la base es el handle aterrizado: los diffs se calculan
    // contra ÉL y sus posiciones no son transportables a otra plantilla. Cuando la base es
    // otra, el handle sale APROXIMADO (le faltan módulos/carga) — y sirve igual, porque no
    // es una certeza sino un candidato: el .js se acepta solo si el SKU coincide, y si no,
    // el mismo texto se usa como query de búsqueda, donde el vecino correcto sí aparece.
    // La CARGA es el único eje de palabra que además es un NÚMERO dentro del handle
    // (`…belastung-pro-palette-1000-kg`). Eso importa porque su salto puede caer en uno de los
    // 24 huecos del catálogo (aterrizando en 400/270/2niv/1mod, el salto a 500 kg no existe)
    // y entonces el metacampo devuelve `handle:null`: el token no se puede aprender NUNCA
    // desde ese producto. En vez de abandonar, se sustituye por valor. Vale para las dos
    // ramas (base aterrizada o no), por eso está aquí fuera.
    var cargaPorNumero = false;
    function aplicaCargaPorNumero(arr) {
      var viejo = String(cfgAterrizada.carga), nuevo = String(cfg.carga);
      for (var i = 0; i < arr.length; i++) {
        // El token seguido de "kg" es el de la carga, no una dimensión.
        if (arr[i] === viejo && arr[i + 1] === 'kg') { arr[i] = nuevo; break; }
      }
      return arr;
    }

    var reemplazos = [], inserciones = {};
    if (!ejeBase) {
      for (var e = 0; e < OG_EJES_APRENDIDOS.length; e++) {
        var eje = OG_EJES_APRENDIDOS[e];
        if (!difiere(eje)) continue;
        var h = ogHandle(eje, defDe(eje).valor(cfg));
        if (!h) {
          if (eje === 'carga') { cargaPorNumero = true; continue; }   // ver arriba
          return null;
        }
        var d = ogDiffTokens(toks, String(h).split('-'));
        if (!d.del && !d.ins.length) continue;                     // sin cambio real
        // No puede pisar los tokens que se sustituyen por número.
        if (d.del && d.start <= pos.iNiv && d.start + d.del > pos.iDim) return null;
        if (d.del) reemplazos.push(d);
        else (inserciones[d.start] = inserciones[d.start] || []).push.apply(inserciones[d.start], d.ins);
      }
      // Dos reemplazos no pueden solaparse, y ninguna inserción puede caer DENTRO de uno.
      reemplazos.sort(function (a, b) { return a.start - b.start; });
      for (var r = 1; r < reemplazos.length; r++) {
        if (reemplazos[r].start < reemplazos[r - 1].start + reemplazos[r - 1].del) return null;
      }
      for (var p in inserciones) {
        for (var q = 0; q < reemplazos.length; q++) {
          if (+p > reemplazos[q].start && +p < reemplazos[q].start + reemplazos[q].del) return null;
        }
      }
    }

    // Se recorre la base una vez: en cada posición van primero las inserciones y luego el
    // reemplazo que empiece ahí (así "…-ebenen-mit-stahlgitterboden-2-felder-…" sale en el
    // orden bueno cuando cambian balda y módulos a la vez).
    var out = [];
    for (var t = 0; t <= toks.length; t++) {
      if (inserciones[t]) out = out.concat(inserciones[t]);
      if (t === toks.length) break;
      var rep = null;
      for (var z = 0; z < reemplazos.length; z++) if (reemplazos[z].start === t) rep = reemplazos[z];
      if (rep) { out = out.concat(rep.ins); t += rep.del - 1; continue; }
      if (t === pos.iDim) {
        if (pos.nDim === 1) out.push(cfg.alto + 'x' + cfg.ancho + 'x' + cfg.fondo);
        else out.push(String(cfg.alto), String(cfg.ancho), String(cfg.fondo));
        t += pos.nDim - 1;
        continue;
      }
      if (t === pos.iNiv) { out.push(String(cfg.niveles)); continue; }
      out.push(toks[t]);
    }

    // ── Ejes aprendidos cuando la BASE no es el handle aterrizado ──────────────
    // Antes esto no se hacía (las ediciones colgaban de `if (!ejeBase)`), y era el fallo que
    // dejaba el precio vacío en cuanto cambiaban DOS ejes de palabra a la vez: al elegir
    // malla la base pasaba a ser el handle de ESE salto, y módulos/protector se quedaban
    // con los valores del producto aterrizado. Medido: pidiendo
    // `…-2-ebenen-mit-stahlgitterboden-1-feld-…` para una config de 4 módulos CON protector.
    //
    // Los índices del diff se calculan contra el handle aterrizado y no son transportables a
    // otra plantilla, así que aquí se aplica por COINCIDENCIA DE TOKENS: se busca la
    // secuencia vieja y se sustituye por la nueva. Si no aparece, se deja como está — el
    // handle sigue siendo un candidato, no una certeza.
    if (ejeBase) {
      var toksAter = String(H0).split('-');
      function sustituye(arr, viejo, nuevo, anclaPrev) {
        if (viejo.length) {
          for (var i = 0; i + viejo.length <= arr.length; i++) {
            var casa = true;
            for (var k = 0; k < viejo.length; k++) if (arr[i + k] !== viejo[k]) { casa = false; break; }
            if (casa) return arr.slice(0, i).concat(nuevo, arr.slice(i + viejo.length));
          }
          return arr;                                   // no está: no tocar
        }
        // Inserción pura (p. ej. añadir `mit-rammschutz`): va detrás de su token previo.
        if (!nuevo.length) return arr;
        var j = anclaPrev ? arr.indexOf(anclaPrev) : -1;
        if (j === -1) return arr;
        return arr.slice(0, j + 1).concat(nuevo, arr.slice(j + 1));
      }
      for (var ea = 0; ea < OG_EJES_APRENDIDOS.length; ea++) {
        var ejeA = OG_EJES_APRENDIDOS[ea];
        if (ejeA === ejeBase || !difiere(ejeA)) continue;
        var hA = ogHandle(ejeA, defDe(ejeA).valor(cfg));
        if (!hA) { if (ejeA === 'carga') cargaPorNumero = true; continue; }
        var dA = ogDiffTokens(toksAter, String(hA).split('-'));
        if (!dA.del && !dA.ins.length) continue;
        out = sustituye(out, toksAter.slice(dA.start, dA.start + dA.del), dA.ins, toksAter[dA.start - 1]);
      }
    }
    if (cargaPorNumero) out = aplicaCargaPorNumero(out);
    return out.join('-');
  }

  // SKU del producto cargado (se emite como productSku; no hay array variants).
  function currentProductSku() {
    var data = window.PalConfiguratorData;
    return (data && data.productSku) ? data.productSku : null;
  }
  // Precio de INICIO instantáneo desde el producto local aterrizado (resuelve directo, sin búsqueda).
  function primeLandingPrice() {
    var data = window.PalConfiguratorData;
    if (!window.Shopify || !data || !data.productHandle) return;
    // ⚠️ Key y SKU se capturan SÍNCRONAMENTE, antes del fetch. Antes la key se calculaba en
    // el .then() con `units[0].config`, que es una referencia VIVA: si el usuario reconfiguraba
    // mientras llegaba la respuesta (conexión lenta), el producto ATERRIZADO quedaba cacheado
    // bajo la config NUEVA → esa config enseñaba el precio y el título del aterrizado, y el
    // botón añadía ESE producto al carrito. Reproducido en live (oct-2026): adicional 400×270
    // mostraba el Grundregal 300×270 a 622,91 € en vez del Anbauregal a 519,90 €.
    var cfg0 = Object.assign({}, units[0].config);
    var ut0  = unitTypeFor(0);
    var key0 = mappingKey(cfg0, ut0);
    var sku0 = derivePalSku(cfg0, ut0);
    fetch(shopRoot() + 'products/' + data.productHandle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var info = buildInfo(d);
        if (!info) return;
        // Misma defensa que el resto de caminos: solo se cachea si el SKU casa EXACTO.
        var v = d && d.variants && d.variants[0];
        if (!v || !sku0 || v.sku !== sku0) return;
        if (!priceCache[key0]) priceCache[key0] = info;
        try { updatePriceAndCart(); } catch (e) {}
      })
      .catch(function () {});
  }

  // Construye el objeto info (precio/variante/stock/imágenes/contenido) desde un producto .js.
  // El .js (NO .json) trae disponibilidad REAL del storefront y precio en céntimos EN LA
  // DIVISA DEL MERCADO al que se pide (por eso resolvemos en el mercado del visitante).
  function buildInfo(d) {
    var v = d && d.variants && d.variants[0];
    if (!v) return null;
    var images = (d.images || []).map(function (src) { return /^https?:/.test(src) ? src : ('https:' + src); });
    return {
      price:     toPresentment(v.price),
      compareAt: (v.compare_at_price && v.compare_at_price > v.price) ? toPresentment(v.compare_at_price) : 0,
      variantId: parseInt(v.id, 10),
      available: v.available !== false,
      images:    images,
      title:       d.title || '',
      description: d.description || ''
    };
  }

  // Resuelve el producto LOCAL del mercado actual por SKU (mercados no-ES, donde el handle
  // español da 404 porque está traducido). Busca por dimensiones en la búsqueda del propio
  // mercado (market-aware) y cruza el SKU exacto (language-neutral). Cachea por key de config.
  function resolveLocalBySku(cfg, unitType, key, cb) {
    var sku = derivePalSku(cfg, unitType);
    if (!sku) { cb(null); return; }
    var root = shopRoot();
    // Foto SÍNCRONA del handle del metacampo: cfg puede llegar por referencia y mutarse
    // mientras esta cadena está en curso. getCachedOrFetch ya clona, esto es la segunda
    // defensa; el check de SKU de más abajo es la definitiva.
    var handleOG0 = handleFromOptionGroups(cfg, unitType) || palComposeHandle(cfg, unitType);
    var dims = cfg.alto + 'x' + cfg.ancho + 'x' + cfg.fondo;
    // Cadena de queries acumulativa. El discriminador de gama es el TAG 'PAL', no una frase
    // descriptiva: el nombre se traduce por mercado (palettenregal, regaly paletowe,
    // raklapos allvany…) y usarlo como filtro falla fuera de un idioma, que es el fallo ya
    // documentado de sin-tornillos.
    var _niv = parseInt(cfg.niveles, 10);
    var queries = [dims + 'cm ' + _niv, dims + ' ' + _niv, dims + 'cm', dims];
    // MÓDULO ADICIONAL: sin esto no se resuelve NUNCA, y el usuario ve el precio vacío.
    //
    // `option_groups_data` publica los saltos de siete ejes, pero NO el de inicial↔adicional,
    // así que el handle compuesto siempre sale con la palabra del módulo INICIAL. Y las
    // queries por dimensiones tampoco lo rescatan: el tope duro de 10 de suggest.json se
    // llena de módulos iniciales (medido: los 10 resultados de "400x270x110" lo son).
    //
    // Tampoco vale sustituir un token en el handle: los handles del adicional NO comparten
    // plantilla entre mercados (medido sobre el catálogo real):
    //    de → palettenregal-ANBAUREGAL-400x270x110-2-ebenen-…
    //    hu → raklapos-allvany-BOVITOMODUL-400-270-110-cm-…
    //    cs/sk → paletovy-regal-PRIDAVNY-MODUL-400x270x110-2-police-…   (otra plantilla entera)
    //    pl → palettenregale-ANBAUREGAL-400-270-110-2-1000-1            (¡en alemán!)
    //
    // Así que se antepone un término de búsqueda por mercado, tomado de esos handles reales.
    // Es vocabulario del CATÁLOGO, no del código: si algún día cambian los títulos hay que
    // revisarlo. Riesgo acotado — un término que no acierte solo devuelve candidatos que el
    // check de SKU descarta, nunca un producto equivocado.
    if ((unitType || 'inicial') === 'adicional') {
      var pais = (window.Shopify && window.Shopify.country) || '';
      var TERMINOS = {
        DE: ['anbauregal'], AT: ['anbauregal'],
        PL: ['anbauregal'],                        // sí: el handle polaco está en alemán
        CZ: ['pridavny modul', 'paletovy regal pridavny'],
        SK: ['pridavny modul', 'paletovy regal pridavny'],
        HU: ['bovitomodul'],
        ES: ['modulo adicional']
      };
      // Si el mercado no está en el mapa se prueban TODOS: son pocas peticiones y solo se
      // lanzan en el camino del adicional.
      var terminos = TERMINOS[pais];
      if (!terminos) {
        terminos = [];
        for (var _p in TERMINOS) { TERMINOS[_p].forEach(function (x) { if (terminos.indexOf(x) === -1) terminos.push(x); }); }
      }
      // Delante de las de dimensiones: son las únicas que pueden traerlo al top-10.
      for (var _t = terminos.length - 1; _t >= 0; _t--) {
        queries.unshift(terminos[_t] + ' ' + dims);
        queries.unshift(terminos[_t] + ' ' + dims + ' ' + _niv);
      }
    }
    // El handle compuesto, con los guiones a espacios, es la query MÁS selectiva que
    // tenemos: lleva ya las palabras traducidas del acabado y de los módulos. Medido en
    // el mercado DE con 600×185×110 / 4 niveles / con malla: las queries por dimensiones
    // dejaban el objetivo FUERA del top-10 (tope duro de suggest.json), y esta lo pone
    // el primero. Va delante de todas.
    if (handleOG0) queries.unshift(String(handleOG0).replace(/-/g, ' '));
    var seen = {};
    function finish(info) {
      if (info) {
        priceCache[key] = info;
        if (key === mappingKey(activeUnit().config, unitTypeFor(activeUnitIdx))) updateGalleryThumbs(info.images || []);
      }
      cb(info || null);
    }
    // Último recurso cuando NINGUNA query trae el candidato en su top-10 (search/suggest.json
    // tiene tope DURO de 10 resultados y con dimensiones compartidas por varias gamas el
    // producto correcto puede quedar fuera por ranking).
    //
    // ⚠️ Aquí NO se usa `viaHreflang()`, que es el último recurso de las otras cinco gamas.
    // Esa función se apoya en que el handle español existe siempre en la raíz de la tienda,
    // y en ESTA gama esa premisa es falsa: el mercado ES está en DRAFT, no se publica
    // hreflang="es" y el handle ES en raíz hace 302 a /de-de/. En su lugar se usa el handle
    // que publica el metacampo `option_groups_data`, ya traducido por mercado.
    function viaOptionGroups() {
      if (!handleOG0) { finish(null); return; }
      fetch(root + 'products/' + handleOG0 + '.js')
        .then(function (r) { if (!r.ok) throw 0; return r.json(); })
        .then(function (d) {
          // Misma defensa que el camino de búsqueda: aceptar solo si el SKU coincide EXACTO
          // con el derivado al entrar, para no cachear un producto ajeno bajo esta key.
          var v = d && d.variants && d.variants[0];
          if (!v || v.sku !== sku) throw 0;
          finish(buildInfo(d));
        })
        .catch(function () { finish(null); });
    }
    function tryQuery(qi) {
      if (qi >= queries.length) { viaOptionGroups(); return; }
      fetch(root + 'search/suggest.json?q=' + encodeURIComponent(queries[qi]) + '&resources[type]=product&resources[limit]=10')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (s) {
          var prods = (s && s.resources && s.resources.results && s.resources.results.products) || [];
          var cands = [];
          prods.forEach(function (p) { if (p && p.handle && !seen[p.handle]) { seen[p.handle] = 1; cands.push(p.handle); } });
          if (!cands.length) { tryQuery(qi + 1); return; }
          var pending = cands.length, done = false;
          cands.forEach(function (h) {
            fetch(root + 'products/' + h + '.js')
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (d) {
                if (done) return;
                var v = d && d.variants && d.variants[0];
                if (v && v.sku === sku) { done = true; finish(buildInfo(d)); return; }
                if (--pending === 0) tryQuery(qi + 1);
              })
              .catch(function () { if (!done && --pending === 0) tryQuery(qi + 1); });
          });
        })
        .catch(function () { tryQuery(qi + 1); });
    }
    tryQuery(0);
  }

  /** Precio+variantId+stock del producto, resuelto SIEMPRE en el mercado del visitante
   *  (cada país ve su producto/precio/divisa/stock). allowSearch habilita la resolución
   *  por SKU en mercados no-ES; el grisado de opciones NO la usa (evita tormenta de red). */
  function getCachedOrFetch(cfg, unitType, cb, allowSearch) {
    // Copia INMUTABLE de la config para esta resolución: cfg puede llegar como referencia
    // viva (units[i].config) que un click posterior muta in situ. El fetch directo de abajo
    // es asíncrono; si su .catch() reenvía el cfg ORIGINAL (por referencia) a resolveLocalBySku
    // tras esa espera, y el usuario reconfiguró mientras tanto, resolveLocalBySku calcularía el
    // SKU/handle de la config NUEVA pero cachearía el resultado bajo el "key" de la config
    // VIEJA → producto ajeno cacheado bajo la key equivocada (y priceCache no tiene TTL, así
    // que el error persiste toda la sesión). Clonando ya en la entrada, todo lo que sigue
    // trabaja sobre un snapshot fijo, coherente con el key que se calcula a continuación.
    cfg = Object.assign({}, cfg);
    var key = mappingKey(cfg, unitType);
    if (priceCache[key]) {
      if (key === mappingKey(activeUnit().config, unitTypeFor(activeUnitIdx))) {
        updateGalleryThumbs(priceCache[key].images || []);
      }
      cb(priceCache[key]);
      return;
    }

    // En esta gama NO se construye handle desde la config (ver el bloque de resolución):
    // se deriva el SKU y se resuelve con él. Si no hay SKU, no hay nada que pedir.
    var sku = derivePalSku(cfg, unitType);
    if (!sku) { cb(null); return; }
    // Atajo: si esta config es un salto de UN eje desde el producto aterrizado, el
    // metacampo ya publica el handle destino traducido y nos ahorra toda la búsqueda.
    // Si el salto es de varios ejes, se compone (ver palComposeHandle): sin esto el
    // camino que queda es la búsqueda, y con dimensiones muy competidas el producto
    // correcto se cae del top-10 de suggest.json — que es el fallo que dejaba el precio
    // vacío en, por ejemplo, 600×185 con 4 niveles y malla.
    var handle = handleFromOptionGroups(cfg, unitType) || palComposeHandle(cfg, unitType);

    // Dedupe de peticiones EN VUELO: priceCache solo se escribe al COMPLETAR, así que dos
    // llamadas concurrentes con la misma key lanzaban dos fetches idénticos. El grisado
    // dispara una comprobación por botón (hasta 19 aquí) y updatePriceAndCart puede
    // encadenarse, con lo que se saturaba el pool HTTP del navegador (~6 conexiones por host)
    // y se retrasaba justo la petición del precio visible.
    // La key incluye allowSearch a propósito: una petición del grisado (que se rinde antes,
    // sin buscar por SKU) NO puede servir de respuesta a una petición de precio.
    // OJO: aquí el flag se evalúa con truthiness (!!allowSearch) y NO con `!== false` como en
    // las otras gamas, porque en Media Carga el grisado llama SIN 4º argumento (undefined) y
    // solo precio/carrito pasan `true` — hay que respetar esa misma semántica.
    var flightKey = key + '|' + (!!allowSearch);
    if (priceInflight[flightKey]) { priceInflight[flightKey].push(cb); return; }
    priceInflight[flightKey] = [cb];
    function settle(info) {
      var waiters = priceInflight[flightKey] || [];
      delete priceInflight[flightKey];
      for (var i = 0; i < waiters.length; i++) waiters[i](info);
    }

    // Sin handle del metacampo no hay atajo: se resuelve por SKU (y solo si se necesita el
    // dato real — el grisado no dispara búsqueda, para no montar una tormenta de red).
    if (!handle) {
      if (allowSearch) resolveLocalBySku(cfg, unitType, key, settle);
      else settle(null);
      return;
    }

    // 1) Atajo: el handle del metacampo ya viene traducido al mercado actual, así que se
    //    pide directo en el root del mercado del visitante.
    fetch(shopRoot() + 'products/' + handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) {
        var info = buildInfo(d);
        if (!info) throw new Error('novariant');
        // Defensa: aceptar solo si el SKU coincide EXACTO con el derivado al entrar, para
        // no cachear un producto ajeno bajo esta key (priceCache no tiene TTL).
        var v = d && d.variants && d.variants[0];
        if (!v || v.sku !== sku) throw new Error('skudistinto');
        priceCache[key] = info;
        if (key === mappingKey(activeUnit().config, unitTypeFor(activeUnitIdx))) updateGalleryThumbs(info.images || []);
        settle(info);
      })
      .catch(function () {
        // 2) El metacampo no cuadraba (desincronizado o forma inesperada): se cae a la
        //    resolución por SKU vía la búsqueda del propio mercado.
        if (allowSearch) resolveLocalBySku(cfg, unitType, key, settle);
        else settle(null);
      });
  }

  /**
   * Tipo de módulo: ahora es EXPLÍCITO por unidad (el usuario lo elige con el
   * selector Inicial/Adicional). Si una unidad no lo tiene fijado (config vieja
   * de URL/historial), se usa la heurística previa como fallback.
   *   · "inicial"   → módulo completo con sus dos bastidores y variante inicial.
   *   · "adicional" → extensión: comparte el bastidor izquierdo y usa variante adicional.
   */
  function unitTypeFor(idx) {
    var u = units[idx];
    if (u && u.tipoModulo) return u.tipoModulo;
    if (idx === 0) return forceAdditionalView ? 'adicional' : 'inicial';
    var ref = units[0].config;
    var cfg = units[idx].config;
    var altoOk  = parseInt(cfg.alto,  10) <= parseInt(ref.alto,  10);
    var fondoOk = String(cfg.fondo) === String(ref.fondo);
    return (altoOk && fondoOk) ? 'adicional' : 'inicial';
  }

  // Un módulo ADICIONAL comparte el bastidor IZQUIERDO con la unidad de su
  // izquierda (idx-1). Físicamente solo encaja si NO es más alto que ese vecino
  // (el bastidor compartido tiene la altura del vecino). La 1ª unidad (idx 0) no
  // tiene vecino izquierdo: es una extensión suelta que se acopla a una
  // estantería existente del cliente, así que no se le aplica la restricción.
  function canBeAdicional(idx) {
    if (idx <= 0) return true;
    return parseInt(units[idx].config.alto, 10) <= parseInt(units[idx - 1].config.alto, 10);
  }

  // Degrada a "inicial" cualquier adicional que haya quedado más alto que su
  // vecino izquierdo (p.ej. tras cambiar alturas o borrar la unidad de al lado).
  function normalizeUnitTypes() {
    for (var i = 1; i < units.length; i++) {
      if (units[i].tipoModulo === 'adicional' && !canBeAdicional(i)) {
        units[i].tipoModulo = 'inicial';
      }
    }
  }

  function showLoading() {
    isLoadingModel = true;
    var el = document.getElementById('loading-overlay');
    if (el) el.classList.remove('hidden');
  }
  function hideLoading() {
    isLoadingModel = false;
    var el = document.getElementById('loading-overlay');
    if (el) el.classList.add('hidden');
  }

  // ─── Ensamblaje analítico de una unidad ─────────────────────────────────────
  // El kit viene horneado y anclado: cada pieza tiene su base en Y=0, el eje del puntal
  // en X=0 y scale/rotación identidad. Así que aquí NO hay que compensar nada: se clona,
  // se pone en su X/Y y ya. El grupo entero se desplaza con group.position.x = xOffset.
  //
  // Un pack de N módulos son N HUECOS CONTIGUOS que comparten bastidor:
  //   inicial   → N+1 bastidores (lleva el suyo de la izquierda)
  //   adicional → N bastidores   (comparte el izquierdo con la unidad previa)
  // Es el `needLeft` de Media Carga generalizado a N huecos.

  // Y del larguero del nivel i de N.
  //
  // El reparto NO es en N+1 huecos iguales. Eso se probó y deja el larguero superior a media
  // altura (60 % en 2 niveles) con un tercio del bastidor vacío por encima, que no es lo que
  // se ve en el producto real.
  //
  // El patrón bueno está MEDIDO sobre los renders del catálogo, detectando por análisis de
  // píxel las filas naranjas (largueros) y la extensión vertical de la estructura. Dos
  // renders independientes por configuración, que coinciden dentro del 0,4 %:
  //
  //   2 niveles (300 cm): largueros al 40,3 % y 79,2 % de la altura del bastidor
  //   4 niveles (600 cm): 18,2 · 40,7 · 63,0 · 85,1 %
  //
  // Leído así: **espaciado uniforme entre largueros, más un REMANENTE por encima del último**.
  // Ese remanente es lo que deja el larguero de arriba cerca de la cabeza del puntal, y
  // encoge al crecer el número de niveles (21 % con 2 → 15 % con 4).
  //
  // Sigue siendo coherente con la capacidad que anuncia la ficha: el SUELO es un plano de
  // almacenaje más (N+1 alturas de palé), y el palé del nivel superior sobresale por encima
  // del bastidor, que es exactamente como funciona una estantería de paletización.
  //
  // Los niveles SOLO pueden anclarse en el ranurado del puntal, así que la cota se redondea a
  // múltiplos de 50 mm. Se clonan en runtime en vez de hornearse: la evidencia del propio
  // repo es clara — las gamas que clonan salieron limpias; las que hornean necesitaron
  // parches, y BULLRACK llegó a producción con 3 cm de dispersión entre baldas.
  var PAL_REMANENTE = { 2: 0.21, 3: 0.18, 4: 0.15 };   // fracción de la altura, por nº de niveles
  function levelY(idx, total, altoKey) {
    var n = Math.max(1, parseInt(total, 10) || 1);
    var frameH = PAL_FRAME_HEIGHTS_M[String(altoKey)] || 3.00498;
    var rem = PAL_REMANENTE[n] !== undefined ? PAL_REMANENTE[n] : 0.18;
    var paso = (frameH * (1 - rem)) / n;
    var pasos = Math.max(1, Math.round((paso * (idx + 1)) / PASO_RANURA));
    return pasos * PASO_RANURA;
  }

  function assembleUnit(unit, unitIdx) {
    if (unit.model) { scene.remove(unit.model); disposeModel(unit.model); unit.model = null; }

    var cfg   = unit.config;
    var alto  = String(cfg.alto);
    var ancho = String(cfg.ancho);
    var niv   = parseInt(cfg.niveles, 10) || 2;
    var mods  = Math.max(1, parseInt(cfg.modulos, 10) || 1);
    var paso  = PAL_PITCH_X[ancho] || PAL_PITCH_X['185'];

    var group = new THREE.Group();
    group.userData.palUnit = true;

    // Búsqueda por IGUALDAD EXACTA del nombre, nunca por substring: así no hay forma de
    // que un futuro 'Larguero1850' se cuele donde toca 'Larguero185'.
    function addPiece(name, x, yOverride) {
      var clone = cloneKitPiece(name);
      if (!clone) { console.warn('[PAL] falta la pieza ' + name); return null; }
      clone.position.set(
        (x || 0) - PAL_KIT_LEFT_X,
        (yOverride !== undefined) ? yOverride : clone.position.y,
        clone.position.z
      );
      clone.traverse(function (c) { if (c.isMesh) { c.castShadow = false; c.receiveShadow = false; } });
      group.add(clone);
      return clone;
    }

    // ── Bastidores: N huecos comparten bastidor con el hueco vecino ──
    // Una unidad "adicional" omite el bastidor izquierdo porque lo comparte con la unidad
    // previa (es la semántica de las familias 2 y 4 del SKU), igual que en Media Carga.
    var desdeHueco = (unitTypeFor(unitIdx) === 'inicial') ? 0 : 1;
    for (var b = desdeHueco; b <= mods; b++) {
      addPiece('Bastidor' + alto, b * paso);
    }

    // ── Niveles: larguero delantero + trasero por hueco, y malla si el acabado la lleva ──
    // Los largueros F y T ya traen su propia profundidad horneada, así que no hay que
    // moverlos en Z. La malla se coloca en el canto superior del perfil y queda enrasada.
    for (var h = 0; h < mods; h++) {
      var xh = h * paso;
      for (var i = 0; i < niv; i++) {
        var yL = levelY(i, niv, alto);
        addPiece('Larguero' + ancho + 'F', xh, yL);
        addPiece('Larguero' + ancho + 'T', xh, yL);
        if (cfg.malla) addPiece('Malla' + ancho, xh, yL + LARGUERO_TOP);
      }
    }

    // ── Protectores de puntal: uno por bastidor dibujado, a nivel de suelo ──
    if (cfg.protector) {
      for (var p = desdeHueco; p <= mods; p++) addPiece('Protector', p * paso, 0);
    }

    group.position.x = unit.xOffset;
    scene.add(group);
    unit.model = group;

    applyColorToUnit(unit);
  }

  // ─── Color por unidad (RAL reales del producto) ──────────────────────────────
  // El GLB llega sin color y SIN NINGUNA TEXTURA, así que basta escribir color/roughness/
  // metalness: no hay mapas que multipliquen el resultado (el gotcha de BULLRACK) ni
  // `depthWrite:false` que haga transparente una balda.
  //
  // ⚠️ El PROTECTOR necesita su rama ANTES del fallback. Si se deja caer al `else`, sale
  // plateado y el amarillo de seguridad desaparece sin que nada lo delate.
  //
  // Se decide por NOMBRE DE NODO (no de material): los nodos del kit son 'Bastidor…',
  // 'Larguero…F/T', 'Malla…' y 'Protector', y ese contrato lo controlamos nosotros.
  function applyColorToUnit(unit) {
    if (!unit.model) return;

    unit.model.traverse(function (child) {
      if (!child.isMesh || !child.material) return;
      // El nombre útil puede estar en la malla o en su nodo padre, según cómo clone three.js
      var nombre = child.name || (child.parent && child.parent.name) || '';
      var hex, props;
      if (/^Protector/i.test(nombre)) {
        hex = PAL_COLORS.protector; props = PROTECTOR_PROPS;
      } else if (/^Larguero/i.test(nombre)) {
        hex = PAL_COLORS.naranja;   props = RAL_PROPS;
      } else {
        // bastidores, malla y cualquier pieza futura: galvanizado
        hex = PAL_COLORS.galva;     props = METAL_PROPS;
      }
      var mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(function (m) {
        if (!m || !m.color) return;
        m.color.setHex(hex);
        if (props.roughness !== undefined && m.roughness !== undefined) m.roughness = props.roughness;
        if (props.metalness !== undefined && m.metalness !== undefined) m.metalness = props.metalness;
        m.needsUpdate = true;
      });
    });
  }

  // ─── Layout multi-unidad ──────────────────────────────────────────────────────
  // Ancho que ocupa una unidad = sus N huecos. El último bastidor no cuenta como ancho
  // propio porque lo comparte con la unidad siguiente si esta es "adicional".
  function moduleWidthM(cfg) {
    var mods = Math.max(1, parseInt(cfg.modulos, 10) || 1);
    return (PAL_PITCH_X[String(cfg.ancho)] || PAL_PITCH_X['185']) * mods;
  }

  function computeUnitOffsets() {
    var cursor = 0;
    for (var i = 0; i < units.length; i++) {
      units[i].xOffset = cursor;
      cursor += moduleWidthM(units[i].config);
      // Dos iniciales seguidas no comparten bastidor: hay que separarlas el ancho de uno
      // para que los perfiles queden tangentes y no solapados.
      if (i + 1 < units.length && unitTypeFor(i + 1) === 'inicial') {
        cursor += PAL_BASTIDOR_WIDTH;
      }
    }
    var halfTotal = cursor / 2;
    for (var j = 0; j < units.length; j++) units[j].xOffset -= halfTotal;
  }

  function recalcUnitPositions() {
    computeUnitOffsets();
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) {
        units[i].model.position.x = units[i].xOffset;
        units[i].model.updateMatrixWorld(true);
      }
    }
  }

  // ─── Pasillos: copias del conjunto completo separadas en profundidad (Z) ──────
  // Cuando bundleQty > 1 se replica el run entero como filas paralelas detrás del
  // original (z negativo), como los pasillos de un almacén. Son clones SOLO
  // visuales: precio, cotas y selección operan únicamente sobre el run primario.
  function bundleZStep() {
    var maxFondoMM = 0;
    for (var i = 0; i < units.length; i++) {
      maxFondoMM = Math.max(maxFondoMM, (PRODUCT_DIMS.depths[String(units[i].config.fondo)] || 600));
    }
    return (maxFondoMM / 1000) + AISLE_GAP_M;
  }

  function clearBundleClones() {
    // Los clones comparten geometría y materiales con el run primario (clone(true)
    // no los duplica), así que NO se hace dispose: solo se quitan de la escena.
    for (var i = 0; i < bundleClones.length; i++) scene.remove(bundleClones[i]);
    bundleClones = [];
  }

  function buildBundleClones() {
    clearBundleClones();
    if (!visualClonesEnabled || bundleQty <= 1) return;
    var step = bundleZStep();
    for (var b = 1; b < bundleQty; b++) {
      var g = new THREE.Group();
      g.userData.mcBundleClone = true;
      for (var u = 0; u < units.length; u++) {
        if (units[u].model) g.add(units[u].model.clone(true));
      }
      g.position.z = -b * step;
      scene.add(g);
      bundleClones.push(g);
    }
  }

  // ─── Reconstrucción de la escena (todas las unidades) ───────────────────────
  // El ensamblaje es analítico y barato (solo clones); cualquier cambio de
  // geometría reconstruye todas las unidades para mantener offsets y bastidores
  // compartidos coherentes.
  function rebuildAllUnits(adjustCam) {
    computeUnitOffsets();
    for (var i = 0; i < units.length; i++) assembleUnit(units[i], i);
    try { updateUnitHighlight(); }    catch(e) { console.error('[MC] updateUnitHighlight', e); }
    try { buildBundleClones(); }      catch(e) { console.error('[MC] buildBundleClones', e); }
    if (adjustCam) { try { adjustCameraToAllUnits(_cameraUserMoved); } catch(e) { console.error('[MC] adjustCamera', e); } }
    else { scene.updateMatrixWorld(true); }
    try { createDimLines(); }         catch(e) { console.error('[MC] createDimLines', e); }
    try { updatePriceAndCart(); }     catch(e) { console.error('[MC] updatePriceAndCart', e); }
    try { updateSpecs(); }            catch(e) { console.error('[MC] updateSpecs', e); }
    var errEl = document.getElementById('model-error');
    if (errEl) errEl.classList.add('hidden');
  }

  function loadAllUnits() {
    showLoading();

    var data = window.PalConfiguratorData;
    var kitUrl = data && (data.glbUrl || data.kitUrl);
    if (!kitUrl) { hideLoading(); return; }

    // Timeout de seguridad: si a los 15 s el kit no cargó, ocultar spinner
    var loadTimeout = setTimeout(function () {
      if (isLoadingModel) {
        console.warn('[PAL] Timeout cargando kit — forzando hideLoading');
        hideLoading();
      }
    }, 15000);

    loadKit(kitUrl, function (err) {
      clearTimeout(loadTimeout);
      if (err) {
        console.error('[PAL] Error cargando kit GLB:', err);
        var errEl = document.getElementById('model-error');
        if (errEl) errEl.classList.remove('hidden');
        hideLoading();
        return;
      }
      try { rebuildAllUnits(true); } catch(e) { console.error('[PAL] Error ensamblando:', e); }
      hideLoading();
    });
  }

  function reloadActiveUnit() {
    if (!kitLoaded) { loadAllUnits(); return; }
    showLoading();
    try { rebuildAllUnits(true); } catch(e) { console.error('[PAL] Error reensamblando:', e); }
    hideLoading();
  }

  // Reensamblado ligero sin mover la cámara (fondo/niveles/tipo)
  function refreshUnitsNoCamera() {
    if (!kitLoaded) return;
    try { rebuildAllUnits(false); } catch(e) { console.error('[PAL] Error reensamblando:', e); }
  }

  function visibleBox(model) {
    var box = new THREE.Box3();
    model.traverse(function (child) {
      if (child.isMesh && child.visible)
        box.union(new THREE.Box3().setFromObject(child));
    });
    if (box.isEmpty()) box.setFromObject(model);
    return box;
  }

  // Factor de altura para la cámara: 1.0 con módulos de 200cm (2.0 m);
  // escala las posiciones fijas de cámara para 250/300cm.
  function camHeightK() {
    var maxH = 0;
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) maxH = Math.max(maxH, visibleBox(units[i].model).max.y);
    }
    var k = (maxH || 2.0) / 2.0;
    return k <= 1 ? 1 : k * 1.08;
  }

  function adjustCameraToAllUnits(preserveZoom) {
    scene.updateMatrixWorld(true);
    var k = camHeightK();
    if (units.length === 1 && bundleQty === 1 && units[0].model) {
      var singleCenter = visibleBox(units[0].model).getCenter(new THREE.Vector3());
      if (preserveZoom) {
        var dir1 = camera.position.clone().sub(controls.target).normalize();
        var dist1 = camera.position.distanceTo(controls.target);
        controls.target.copy(singleCenter);
        camera.position.copy(singleCenter).addScaledVector(dir1, dist1);
      } else {
        controls.target.set(singleCenter.x, singleCenter.y, singleCenter.z);
        camera.position.set(2.8 * k, 2.4 * k, 3.8 * k);
      }
      controls.update();
      return;
    }
    var totalBox = new THREE.Box3();
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) totalBox.union(visibleBox(units[i].model));
    }
    for (var ci = 0; ci < bundleClones.length; ci++) totalBox.union(visibleBox(bundleClones[ci]));
    if (totalBox.isEmpty()) return;
    var center = totalBox.getCenter(new THREE.Vector3());
    var size   = totalBox.getSize(new THREE.Vector3());
    var reach  = Math.max(size.x, size.z);
    if (preserveZoom) {
      var dir2 = camera.position.clone().sub(controls.target).normalize();
      var dist2 = camera.position.distanceTo(controls.target);
      controls.target.copy(center);
      camera.position.copy(center).addScaledVector(dir2, dist2);
    } else {
      controls.target.set(center.x, center.y, center.z);
      camera.position.set(center.x + reach * 0.8 + 2.5 * k, center.y + 1.8 * k + size.z * 0.25, center.z + reach * 0.9 + 3.0 * k);
    }
    controls.update();
  }

  // ─── Animacion suave de camara (easeInOutQuad) ────────────────────────────────
  function animateCameraTo(targetPos, targetLook, ms) {
    var startPos    = camera.position.clone();
    var startTarget = controls.target.clone();
    var duration    = ms || 420;
    var startTime   = null;
    function step(ts) {
      if (!startTime) startTime = ts;
      var elapsed = ts - startTime;
      var t = Math.min(elapsed / duration, 1);
      t = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      camera.position.lerpVectors(startPos, targetPos, t);
      controls.target.lerpVectors(startTarget, targetLook, t);
      controls.update();
      if (elapsed < duration) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function updateUnitHighlight() {
    for (var i = 0; i < units.length; i++) {
      var u = units[i]; if (!u.model) continue;
      var isActive = (i === activeUnitIdx);
      u.model.traverse(function (child) {
        if (child.isMesh && child.material) {
          var mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach(function (m) { m.opacity = isActive ? 1.0 : 0.5; m.transparent = !isActive; });
        }
      });
    }
  }

  function addFloor() {
    var grid = new THREE.GridHelper(24, 24, 0xC8C8C8, 0xD8D8D8);
    grid.material.opacity = 0.30; grid.material.transparent = true;
    scene.add(grid);

    var subgrid = new THREE.GridHelper(24, 120, 0xE0E0E0, 0xE0E0E0);
    subgrid.material.opacity = 0.12; subgrid.material.transparent = true;
    scene.add(subgrid);

    addFloorLogo();
  }

  function addFloorLogo() {
    var logoUrl = (window.PalConfiguratorData && window.PalConfiguratorData.logoUrl)
      ? window.PalConfiguratorData.logoUrl
      : 'https://cdn.shopify.com/s/files/1/0999/8834/1123/files/LogoMaster_26_square.png?v=1780060507';

    var img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = function () {
      try {
        var w = img.naturalWidth  || img.width;
        var h = img.naturalHeight || img.height;
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        var imageData = ctx.getImageData(0, 0, w, h);
        var d = imageData.data;
        for (var i = 0; i < d.length; i += 4) {
          var lum = d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
          if (lum > 200) { d[i+3] = 0; }
          else if (lum > 140) { d[i+3] = Math.round((1 - (lum - 140) / 60) * d[i+3]); }
        }
        ctx.putImageData(imageData, 0, 0);
        var tex = new THREE.CanvasTexture(canvas);
        buildFloorLogoMesh(tex, THREE.NormalBlending, 0.084);
      } catch (e) {
        try {
          var texFallback = new THREE.Texture(img);
          texFallback.needsUpdate = true;
          buildFloorLogoMesh(texFallback, THREE.MultiplyBlending, 0.18);
        } catch(e2) {
          console.warn('[PAL] Logo suelo no disponible:', e2);
        }
      }
    };

    img.onerror = function () {
      var img2 = new Image();
      img2.onload = function () {
        try {
          var tex2 = new THREE.Texture(img2);
          tex2.needsUpdate = true;
          buildFloorLogoMesh(tex2, THREE.MultiplyBlending, 0.18);
        } catch(e){}
      };
      img2.src = logoUrl + (logoUrl.indexOf('?') > -1 ? '&nc=1' : '?nc=1');
    };

    img.src = logoUrl;
  }

  function buildFloorLogoMesh(tex, blending, opacity) {
    var mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, opacity: opacity,
      depthWrite: false, blending: blending
    });
    var mesh = new THREE.Mesh(new THREE.PlaneGeometry(4.0, 4.0), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.003, 0);
    mesh.renderOrder = 1;
    scene.add(mesh);
  }

  // Contenedor de las etiquetas de cota, para poder crearlas BAJO DEMANDA (ver dimLabel).
  var dimLabelBox = null;

  // Devuelve la etiqueta de cota `id`, creándola la primera vez que hace falta.
  //
  // ⚠️ Antes se preinstanciaban las 102 (2 fijas + MAX_UNITS × 2), aunque lo normal es tener
  // UNA unidad. PageSpeed lo señalaba en "Optimizar tamaño del DOM": de 1.265 elementos de la
  // página, `#viewer-3d-wrap` acumulaba 109 hijos — el nodo con más hijos de todo el documento.
  // Crearlas al vuelo quita ~100 nodos del arranque y no cambia nada más: `place()` las pide
  // por id, y las que no existen es porque no se están usando.
  function dimLabel(id) {
    var el = dimLabelEls[id];
    if (el) return el;
    if (!dimLabelBox) return null;
    el = document.createElement('div');
    el.id = id; el.className = 'dim-label'; el.style.display = 'none';
    dimLabelBox.appendChild(el); dimLabelEls[id] = el;
    return el;
  }

  function initDimLabels(container) {
    dimLabelBox = container;
    ['dim-height', 'dim-width-total'].forEach(function (id) {
      var el = document.createElement('div');
      el.id = id; el.className = 'dim-label'; el.style.display = 'none';
      container.appendChild(el); dimLabelEls[id] = el;
    });
    // Solo la unidad 0: el resto se crea cuando el usuario añade módulos.
    for (var i = 0; i < 1; i++) {
      ['dim-width-', 'dim-depth-'].forEach(function (pfx) {
        var el = document.createElement('div');
        el.id = pfx + i; el.className = 'dim-label'; el.style.display = 'none';
        container.appendChild(el); dimLabelEls[pfx + i] = el;
      });
    }
  }

  function createDimLines() {
    if (dimLinesGroup) scene.remove(dimLinesGroup);
    dimLinesGroup = new THREE.Group();
    var boxes = [];
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) boxes.push({ box: visibleBox(units[i].model), unit: units[i], idx: i });
    }
    if (!boxes.length) { scene.add(dimLinesGroup); return; }

    var mat       = new THREE.LineBasicMaterial({ color: 0x666666 });
    var matTotal  = new THREE.LineBasicMaterial({ color: 0xF24507 });
    var matDepth  = new THREE.LineBasicMaterial({ color: 0x555555, transparent: true, opacity: 0.65 });

    function L(pts, m) { return new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), m || mat); }
    function T(c, ax, h, m) { var p = [c.clone(), c.clone()]; p[0][ax] -= h; p[1][ax] += h; return L(p, m); }

    var GAP = 0.10;
    function LG(a, b, m) {
      var mid = new THREE.Vector3((a.x+b.x)/2, (a.y+b.y)/2, (a.z+b.z)/2);
      var dir = b.clone().sub(a).normalize();
      var a2  = mid.clone().sub(dir.clone().multiplyScalar(GAP));
      var b2  = mid.clone().add(dir.clone().multiplyScalar(GAP));
      dimLinesGroup.add(L([a, a2], m));
      dimLinesGroup.add(L([b2, b], m));
    }

    var tk  = 0.06;
    var Y   = 0.01;
    var totalBox = new THREE.Box3();
    boxes.forEach(function(b) { totalBox.union(b.box); });

    var Z_IND   = totalBox.max.z + 0.08;
    var Z_TOTAL = totalBox.max.z + 0.18;

    // ── COTA DE ALTURA (izquierda, vertical — línea rota) ──
    var hx = totalBox.min.x - 0.06;
    var hA = new THREE.Vector3(hx, totalBox.min.y, Z_IND);
    var hB = new THREE.Vector3(hx, totalBox.max.y, Z_IND);
    LG(hA, hB);
    dimLinesGroup.add(T(hA, 'y', tk));
    dimLinesGroup.add(T(hB, 'y', tk));

    // ── ANCHURAS INDIVIDUALES (líneas rotas en el suelo) ──
    boxes.forEach(function(b) {
      var wA = new THREE.Vector3(b.box.min.x, Y, Z_IND);
      var wB = new THREE.Vector3(b.box.max.x, Y, Z_IND);
      LG(wA, wB);
      dimLinesGroup.add(T(wA, 'x', tk));
      dimLinesGroup.add(T(wB, 'x', tk));
    });

    // ── ANCHURA TOTAL (línea rota naranja, solo >1 unidad) ──
    if (boxes.length > 1) {
      var tA = new THREE.Vector3(totalBox.min.x, Y, Z_TOTAL);
      var tB = new THREE.Vector3(totalBox.max.x, Y, Z_TOTAL);
      LG(tA, tB, matTotal);
      dimLinesGroup.add(T(tA, 'x', tk, matTotal));
      dimLinesGroup.add(T(tB, 'x', tk, matTotal));
      var matConn = new THREE.LineBasicMaterial({ color: 0x888888, transparent: true, opacity: 0.35 });
      dimLinesGroup.add(L([tA, new THREE.Vector3(totalBox.min.x, Y, Z_IND)], matConn));
      dimLinesGroup.add(L([tB, new THREE.Vector3(totalBox.max.x, Y, Z_IND)], matConn));
    }

    // ── PROFUNDIDAD POR UNIDAD (línea rota lateral) ──
    boxes.forEach(function(b) {
      var bx = b.box;
      var dx = bx.max.x + 0.10;
      var dA = new THREE.Vector3(dx, Y, bx.max.z);
      var dB = new THREE.Vector3(dx, Y, bx.min.z);
      LG(dA, dB, matDepth);
      dimLinesGroup.add(T(dA, 'z', tk, matDepth));
      dimLinesGroup.add(T(dB, 'z', tk, matDepth));
      dimLinesGroup.add(L([new THREE.Vector3(bx.max.x, Y, bx.max.z), dA], matDepth));
    });

    scene.add(dimLinesGroup);
  }

  function updateDimLabels() {
    if (!dimLabelEls['dim-height']) return;
    var canvas = document.getElementById('configurator-canvas'); if (!canvas) return;
    var w = canvas.clientWidth; var h = canvas.clientHeight;
    var boxes = [];
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) boxes.push({ box: visibleBox(units[i].model), unit: units[i], idx: i });
    }
    if (!boxes.length) return;
    var totalBox = new THREE.Box3();
    boxes.forEach(function(b) { totalBox.union(b.box); });

    function scr(v) { var p = v.clone().project(camera); return { x: Math.round((p.x+1)*w/2), y: Math.round((1-p.y)*h/2), ok: p.z<1 }; }
    function place(id, v3, text) {
      var el = dimLabel(id); if (!el) return;   // se crea al vuelo la primera vez
      var s = scr(v3);
      var M = 64;
      if (!s.ok || s.x < -M || s.x > w + M || s.y < -M || s.y > h + M) {
        el.style.display = 'none'; return;
      }
      // Clamp suave: si proyecta algo fuera pero dentro del margen, pegar al borde
      var cx = Math.min(Math.max(s.x, 6), w - 6);
      var cy = Math.min(Math.max(s.y, 6), h - 6);
      el.style.display = 'block'; el.style.left = cx + 'px'; el.style.top = cy + 'px'; el.textContent = text;
    }
    function hide(id) { var el = dimLabelEls[id]; if (el) el.style.display='none'; }

    var Y       = 0.01;
    var Z_IND   = totalBox.max.z + 0.08;
    var Z_TOTAL = totalBox.max.z + 0.18;

    // ── Altura (izquierda, mitad del modelo) ──
    var frameH = 0;
    for (var fh = 0; fh < units.length; fh++) frameH = Math.max(frameH, getFrameHeight(units[fh].config));
    place('dim-height',
      new THREE.Vector3(totalBox.min.x - 0.06, (totalBox.min.y + totalBox.max.y) / 2, Z_IND),
      frameH + ' mm');

    // ── Anchuras individuales ──
    var Yind = Y + 0.08;
    for (var i = 0; i < MAX_UNITS; i++) {
      var found = null;
      for (var j = 0; j < boxes.length; j++) { if (boxes[j].idx === i) { found = boxes[j]; break; } }
      if (!found) { hide('dim-width-'+i); continue; }
      // El ancho de la cota es el del PACK ENTERO (N módulos), no el de un módulo suelto:
      // la línea ya abarca toda la unidad, así que rotularla con 1850 mm mientras el panel
      // dice 3700 es contradictorio. Aquí una "unidad" son N huecos contiguos — es la
      // diferencia con Media Carga, de donde se clonó este bloque.
      var wVal = (PRODUCT_DIMS.widths[found.unit.config.ancho] || 0) *
                 (parseInt(found.unit.config.modulos, 10) || 1);
      if (!wVal) { hide('dim-width-'+i); continue; }
      place('dim-width-'+i,
        new THREE.Vector3((found.box.min.x+found.box.max.x)/2, Yind, Z_IND),
        wVal + ' mm');
    }

    // ── Anchura total (naranja, solo >1 unidad) ──
    if (boxes.length > 1) {
      var totalW = 0;
      // Igual que arriba: cada unidad aporta sus N módulos, no uno solo.
      for (var k = 0; k < units.length; k++) {
        totalW += (PRODUCT_DIMS.widths[units[k].config.ancho] || 0) *
                  (parseInt(units[k].config.modulos, 10) || 1);
      }
      for (var k2 = 1; k2 < units.length; k2++) {
        if (unitTypeFor(k2) === 'inicial') totalW += Math.round(PAL_BASTIDOR_WIDTH * 1000);
      }
      place('dim-width-total',
        new THREE.Vector3((totalBox.min.x+totalBox.max.x)/2, Y, Z_TOTAL),
        totalW + ' mm');
    } else {
      hide('dim-width-total');
    }

    // ── Profundidad por unidad ──
    var allSameDepth = units.length > 0 && units.every(function(u) { return u.config.fondo === units[0].config.fondo; });
    var lastBoxIdx = boxes.length > 0 ? boxes[boxes.length - 1].idx : -1;
    for (var m = 0; m < MAX_UNITS; m++) {
      var fb = null;
      for (var n = 0; n < boxes.length; n++) { if (boxes[n].idx === m) { fb = boxes[n]; break; } }
      if (!fb) { hide('dim-depth-'+m); continue; }
      if (allSameDepth && m !== lastBoxIdx) { hide('dim-depth-'+m); continue; }
      place('dim-depth-'+m,
        new THREE.Vector3(fb.box.max.x + 0.18, Y, (fb.box.min.z+fb.box.max.z)/2),
        PRODUCT_DIMS.depths[fb.unit.config.fondo]+' mm');
    }

    // Mobile (≤600px): en multi-unidad ocultar anchos individuales (el total ya se muestra)
    var mobileMode = window.innerWidth <= 600;
    if (mobileMode && boxes.length > 1) {
      for (var q = 0; q < MAX_UNITS; q++) {
        var dw = dimLabelEls['dim-width-' + q]; if (dw) dw.style.display = 'none';
      }
    }
  }

  function updateActiveUnitArrow() {
    if (!camera || !renderer) return;
    var wrap = document.getElementById('viewer-3d-wrap');
    if (!wrap) return;
    var arrow = document.getElementById('unit-active-arrow');
    if (!arrow) {
      arrow = document.createElement('div');
      arrow.id = 'unit-active-arrow';
      arrow.className = 'unit-active-arrow';
      arrow.textContent = '▼';
      wrap.appendChild(arrow);
    }
    var unit = activeUnit();
    if (!unit || !unit.model) { arrow.style.display = 'none'; return; }
    var box = visibleBox(unit.model);
    if (box.isEmpty()) { arrow.style.display = 'none'; return; }
    var center = box.getCenter(new THREE.Vector3());
    var p3 = new THREE.Vector3(center.x, box.max.y + 0.2, center.z);
    var v = p3.clone().project(camera);
    if (v.z > 1) { arrow.style.display = 'none'; return; }
    var rect = renderer.domElement.getBoundingClientRect();
    var sx = (v.x + 1) / 2 * rect.width  + rect.left;
    var sy = (-v.y + 1) / 2 * rect.height + rect.top;
    var parentRect = wrap.getBoundingClientRect();
    arrow.style.display = 'block';
    arrow.style.left = (sx - parentRect.left) + 'px';
    arrow.style.top  = (sy - parentRect.top)  + 'px';
  }

  // ─── Botones de vista de camara + undo/redo ───────────────────────────────────
  function makeCamIcon(svgPath, label) {
    var btn = document.createElement('button');
    btn.className = 'camera-btn';
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + svgPath + '</svg>'
                  + '<span class="camera-btn-label">' + label + '</span>';
    return btn;
  }

  function addCameraButtons(container) {
    var wrapper = document.createElement('div');
    wrapper.className = 'camera-buttons';

    var views = [
      {
        svg: '<path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
        label: '3D', title: t('cam_perspective'),
        getPos:  function () { var k = camHeightK(); return new THREE.Vector3(2.8 * k, 2.4 * k, 3.8 * k); },
        getLook: function () { return currentModel() ? visibleBox(currentModel()).getCenter(new THREE.Vector3()) : new THREE.Vector3(0,1.0,0); }
      },
      {
        svg: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><polyline points="8 12 12 8 16 12"/>',
        label: t('cam_lbl_front'), title: t('cam_front'),
        getPos:  function () { var k = camHeightK(); return new THREE.Vector3(0, 1.0 * k, 5.5 * k); },
        getLook: function () { var k = camHeightK(); return new THREE.Vector3(0, 1.0 * k, 0); }
      },
      {
        svg: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/><polyline points="12 8 16 12 12 16"/>',
        label: t('cam_lbl_side'), title: t('cam_side'),
        getPos:  function () { var k = camHeightK(); return new THREE.Vector3(5.5 * k, 1.0 * k, 0); },
        getLook: function () { var k = camHeightK(); return new THREE.Vector3(0, 1.0 * k, 0); }
      },
      {
        svg: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>',
        label: t('cam_lbl_top'), title: t('cam_top'),
        getPos:  function () { return new THREE.Vector3(0, 7.0, 0.01); },
        getLook: function () { return new THREE.Vector3(0, 0, 0); }
      }
    ];

    views.forEach(function (v) {
      var btn = makeCamIcon(v.svg, v.label);
      btn.title = v.title;
      btn.addEventListener('click', function () { animateCameraTo(v.getPos(), v.getLook()); });
      wrapper.appendChild(btn);
    });

    var sep1 = document.createElement('span'); sep1.className = 'camera-btn-sep'; wrapper.appendChild(sep1);

    var undoBtn = makeCamIcon('<path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>', t('cam_lbl_undo'));
    undoBtn.id = 'btn-undo'; undoBtn.className += ' camera-btn--history';
    undoBtn.title = t('cam_undo'); undoBtn.disabled = true;
    undoBtn.addEventListener('click', undo); wrapper.appendChild(undoBtn);

    var redoBtn = makeCamIcon('<path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/>', t('cam_lbl_redo'));
    redoBtn.id = 'btn-redo'; redoBtn.className += ' camera-btn--history';
    redoBtn.title = t('cam_redo'); redoBtn.disabled = true;
    redoBtn.addEventListener('click', redo); wrapper.appendChild(redoBtn);

    var sep2 = document.createElement('span'); sep2.className = 'camera-btn-sep'; wrapper.appendChild(sep2);

    var resetBtn = makeCamIcon('<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>', t('cam_lbl_reset'));
    resetBtn.className += ' camera-btn--reset';
    resetBtn.title = t('cam_reset');
    resetBtn.addEventListener('click', function () {
      for (var i = 0; i < units.length; i++) {
        if (units[i].model) { scene.remove(units[i].model); disposeModel(units[i].model); }
      }
      clearBundleClones();
      // Reset al default del producto: tipo de la unidad 0 según el contexto de la ficha.
      var defTipo0 = forceAdditionalView ? 'adicional' : 'inicial';
      units = [{ id: 1, tipoModulo: defTipo0,
                 config: { alto: '300', ancho: '185', fondo: FONDO_FIJO, niveles: 2, modulos: 1,
                           malla: false, protector: false, carga: '1000' },
                 model: null, xOffset: 0 }];
      activeUnitIdx = 0; bundleQty = 1; configHistory = []; historyPointer = -1;
      syncPanelToUnit(); renderUnitTabs(); reloadActiveUnit();
      syncQtyInput(); pushHistory();
    });
    wrapper.appendChild(resetBtn);

    var sep2b = document.createElement('span'); sep2b.className = 'camera-btn-sep'; wrapper.appendChild(sep2b);

    var fitBtn = makeCamIcon(
      '<path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>',
      t('cam_lbl_fit')
    );
    fitBtn.title = t('cam_title_fit');
    fitBtn.addEventListener('click', function () {
      var totalBox = new THREE.Box3();
      for (var i = 0; i < units.length; i++) {
        if (units[i].model) totalBox.union(visibleBox(units[i].model));
      }
      if (totalBox.isEmpty()) return;
      var center = totalBox.getCenter(new THREE.Vector3());
      var size   = totalBox.getSize(new THREE.Vector3());
      var dist   = Math.max(size.x, size.y, size.z) * 1.5 + 2.0;
      animateCameraTo(
        new THREE.Vector3(center.x + size.x * 0.7 + dist * 0.4, center.y + dist * 0.5, center.z + dist * 0.75),
        center,
        500
      );
    });
    wrapper.appendChild(fitBtn);

    var sep3 = document.createElement('span'); sep3.className = 'camera-btn-sep'; wrapper.appendChild(sep3);

    var dimsVisible = true;
    var dimsBtn = makeCamIcon('<line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="3" y2="18"/><line x1="21" y1="6" x2="21" y2="18"/><line x1="9" y1="8" x2="9" y2="10"/><line x1="15" y1="8" x2="15" y2="10"/>', t('cam_lbl_dims'));
    dimsBtn.id = 'btn-toggle-dims';
    dimsBtn.title = t('cam_title_dims');
    dimsBtn.classList.add('active');
    dimsBtn.addEventListener('click', function () {
      dimsVisible = !dimsVisible;
      if (dimLinesGroup) dimLinesGroup.visible = dimsVisible;
      document.querySelectorAll('.dim-label').forEach(function (el) { el.style.visibility = dimsVisible ? '' : 'hidden'; });
      dimsBtn.classList.toggle('active', dimsVisible);
    });
    wrapper.appendChild(dimsBtn);

    var sep4 = document.createElement('span'); sep4.className = 'camera-btn-sep'; wrapper.appendChild(sep4);

    var clonesBtn = makeCamIcon('<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>', t('cam_lbl_clone'));
    clonesBtn.id = 'btn-toggle-clones';
    clonesBtn.title = t('cam_title_clone');
    clonesBtn.addEventListener('click', function () {
      visualClonesEnabled = !visualClonesEnabled;
      clonesBtn.classList.toggle('active', visualClonesEnabled);
      buildBundleClones();
      adjustCameraToAllUnits(false);
    });
    wrapper.appendChild(clonesBtn);

    container.appendChild(wrapper);
  }

  // ─── Historial undo/redo ──────────────────────────────────────────────────────
  function snapshotUnitsConfig() {
    return {
      units: units.map(function (u) {
        return { id: u.id, tipoModulo: u.tipoModulo,
                 config: { alto: u.config.alto, ancho: u.config.ancho, fondo: u.config.fondo,
                           niveles: u.config.niveles, modulos: u.config.modulos,
                           malla: u.config.malla, protector: u.config.protector, carga: u.config.carga } };
      }),
      activeUnitIdx: activeUnitIdx,
      bundleQty: bundleQty
    };
  }

  function pushHistory() {
    configHistory.splice(historyPointer + 1);
    configHistory.push(snapshotUnitsConfig());
    historyPointer = configHistory.length - 1;
    if (configHistory.length > 30) { configHistory.shift(); historyPointer = 29; }
    updateHistoryButtons();
  }

  function applyHistorySnapshot(snap) {
    for (var i = 0; i < units.length; i++) {
      if (units[i].model) { scene.remove(units[i].model); disposeModel(units[i].model); }
    }
    units = snap.units.map(function (su) {
      return { id: su.id, tipoModulo: su.tipoModulo, config: Object.assign({}, su.config), model: null, xOffset: 0 };
    });
    activeUnitIdx = Math.min(snap.activeUnitIdx, units.length - 1);
    bundleQty = snap.bundleQty || 1;
    syncPanelToUnit(); renderUnitTabs(); reloadActiveUnit();
    syncQtyInput(); updateHistoryButtons();
  }

  function undo() { if (historyPointer <= 0) return; historyPointer--; applyHistorySnapshot(configHistory[historyPointer]); }
  function redo() { if (historyPointer >= configHistory.length - 1) return; historyPointer++; applyHistorySnapshot(configHistory[historyPointer]); }

  function updateHistoryButtons() {
    var u = document.getElementById('btn-undo'); var r = document.getElementById('btn-redo');
    if (u) u.disabled = historyPointer <= 0;
    if (r) r.disabled = historyPointer >= configHistory.length - 1;
  }

  // ─── Grupos de carrito ───────────────────────────────────────────────────────
  // La primera unidad es el módulo INICIAL (familias 1 y 3 del SKU); el resto son módulos
  // ADICIONALES (familias 2 y 4) agrupados por configuración idéntica.
  function buildCartGroups() {
    var groups = [];
    units.forEach(function (u, idx) {
      var type = unitTypeFor(idx);
      var found = false;
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].unitType === type && configsMatch(groups[i].config, u.config)) {
          groups[i].count++; found = true; break;
        }
      }
      if (!found) groups.push({ config: Object.assign({}, u.config), unitType: type, count: 1 });
    });
    // Cada conjunto (bundle) se compra bundleQty veces
    if (bundleQty > 1) groups.forEach(function (g) { g.count *= bundleQty; });
    return groups;
  }

  // ─── Carrito permalink — para el botón de compra en el PDF ──────────────────
  function buildCartPermalink() {
    var groups = buildCartGroups();
    var items = [];
    groups.forEach(function (g) {
      var cached = priceCache[mappingKey(g.config, g.unitType)];
      if (cached && cached.variantId) items.push(cached.variantId + ':' + g.count);
    });
    if (!items.length) return '';
    return window.location.origin + '/cart/' + items.join(',');
  }

  // ─── Ficha técnica / Presupuesto ────────────────────────────────────────────
  function captureScreenshot() { generateQuoteDocument(); }

  function captureSnapshotWithDims() {
    renderer.render(scene, camera);
    var glCanvas = renderer.domElement;
    var out = document.createElement('canvas');
    out.width = glCanvas.width; out.height = glCanvas.height;
    var ctx = out.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(glCanvas, 0, 0);

    var cRect = glCanvas.getBoundingClientRect();
    if (!cRect.width || !cRect.height) return out.toDataURL('image/png');
    var sx = out.width / cRect.width, sy = out.height / cRect.height;

    Object.keys(dimLabelEls).forEach(function (id) {
      var el = dimLabelEls[id];
      if (!el || el.style.display === 'none') return;
      var text = el.textContent; if (!text) return;
      var r = el.getBoundingClientRect();
      var x = (r.left + r.width / 2 - cRect.left) * sx;
      var y = (r.top + r.height / 2 - cRect.top) * sy;
      if (x < 0 || x > out.width || y < 0 || y > out.height) return;

      var isTotal = (id === 'dim-width-total');
      var fs = Math.round((isTotal ? 13 : 12) * sx);
      ctx.font = (isTotal ? '800 ' : '700 ') + fs + 'px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.lineWidth = Math.max(3, 4 * sx);
      ctx.strokeText(text, x, y);
      ctx.fillStyle = isTotal ? '#F24507' : '#1A1A18';
      ctx.fillText(text, x, y);
    });
    return out.toDataURL('image/png');
  }

  function generateQuoteDocument() {
    var imgDataUrl = captureSnapshotWithDims();

    var data      = window.PalConfiguratorData || {};
    var currency  = curCode();
    var logoUrl   = data.logoUrl || '';
    var titleEl   = document.querySelector('.rc-prod-title') || document.querySelector('.product-title');
    var docTitle  = titleEl ? titleEl.textContent.trim() : 'Estantería para palés';
    var today     = new Date();
    var dateStr   = today.toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', year:'numeric' });
    var refStr    = 'MC-' + today.getFullYear() + (today.getMonth()+1+'').padStart(2,'0') + (today.getDate()+'').padStart(2,'0');

    var cartUrlRaw = buildCartPermalink();
    var cartUrl    = cartUrlRaw ? addUtmParams(cartUrlRaw) : '';
    var shareUrl   = buildShareUrl();

    var unitRows = units.map(function(u, idx) {
      var cfg     = u.config;
      var type    = unitTypeFor(idx);
      var cached  = priceCache[mappingKey(cfg, type)];
      var priceEur = cached ? (vatCents(cached.price) / 100).toLocaleString('es-ES',{minimumFractionDigits:2}) : '—';
      return {
        tipo_mod:  type === 'inicial' ? t('pdf_mod_inicial') : t('pdf_mod_adicional'),
        alto:      cfg.alto,
        ancho:     cfg.ancho,
        fondo:     cfg.fondo,
        niveles:   cfg.niveles,
        balda:     baldaDisplay(cfg.malla),
        modulos:   String(cfg.modulos || 1),
        protector: siNo(cfg.protector),
        pales:     String(palesTotales(cfg)),
        carga:     cargaPorPale(cfg) + ' kg',
        price:     priceEur,
        priceCents: cached ? vatCents(cached.price) : 0
      };
    });
    var totalCents = unitRows.reduce(function(s,r){ return s + r.priceCents; }, 0);
    var totalStr = totalCents > 0 ? (totalCents/100).toLocaleString('es-ES',{minimumFractionDigits:2}) + ' ' + currency : '—';

    var cfg     = activeUnit().config;
    var height  = units.reduce(function (m, u) { return Math.max(m, getFrameHeight(u.config)); }, 0);
    var allDepths    = units.map(function(u) { return u.config.fondo; });
    var uniqueDepths = allDepths.filter(function(d, i, a) { return a.indexOf(d) === i; });
    var cap     = cargaPorPale(cfg);

    // Ancho total: cada unidad aporta sus N módulos de luz libre
    var totalWidthMM = units.reduce(function(s, u) {
      return s + (PRODUCT_DIMS.widths[u.config.ancho] || 0) * (parseInt(u.config.modulos, 10) || 1);
    }, 0);
    for (var gi = 1; gi < units.length; gi++) {
      if (unitTypeFor(gi) === 'inicial') totalWidthMM += Math.round(PAL_BASTIDOR_WIDTH * 1000);
    }
    var depthDisplayMM = uniqueDepths.length === 1
      ? String(PRODUCT_DIMS.depths[uniqueDepths[0]] || Number(uniqueDepths[0]) * 10)
      : uniqueDepths.map(function(d) { return PRODUCT_DIMS.depths[d] || Number(d) * 10; }).join('/');

    var unitRowsHtml = unitRows.map(function(r, i) {
      return '<tr><td>' + (i+1) + '</td>' +
        '<td>' + r.tipo_mod + '</td>' +
        '<td>' + r.alto + ' cm</td>' +
        '<td>' + r.ancho + ' cm</td>' +
        '<td>' + r.fondo + ' cm</td>' +
        '<td>' + r.niveles + '</td>' +
        '<td>' + r.balda + '</td>' +
        '<td>' + r.carga + '</td>' +
        '<td class="price-cell">' + (r.price !== '—' ? r.price + ' ' + currency : '—') + '</td>' +
      '</tr>';
    }).join('');

    var docLang = (window.Shopify && Shopify.locale) ? String(Shopify.locale).slice(0, 2) : 'es';
    var html = '<!DOCTYPE html><html lang="' + docLang + '"><head><meta charset="UTF-8">' +
      '<title>' + t('spec_sheet_short') + ' — ' + docTitle + '</title>' +
      '<style>' +
        '@import url(\'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap\');' +
        '*{box-sizing:border-box;margin:0;padding:0}' +
        'body{font-family:Inter,system-ui,sans-serif;color:#1A1A18;background:#fff;font-size:13px}' +
        '.page{max-width:210mm;margin:0 auto;padding:14mm 14mm 10mm}' +
        '.hdr{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #F24507;padding-bottom:12px;margin-bottom:18px}' +
        '.hdr-logo{height:44px;object-fit:contain}' +
        '.hdr-logo-fallback{font-size:20px;font-weight:800;color:#F24507}' +
        '.hdr-right{text-align:right}' +
        '.hdr-right h1{font-size:18px;font-weight:800;color:#1A1A18;line-height:1.2}' +
        '.hdr-right .meta{font-size:11px;color:#9A9690;margin-top:3px}' +
        '.main-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px}' +
        '.snapshot{width:100%;border-radius:4px;border:1px solid #E0DDD8;display:block}' +
        '.cfg-section h2{font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#9A9690;font-weight:700;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #E0DDD8}' +
        '.cfg-table{width:100%;border-collapse:collapse}' +
        '.cfg-table td{padding:5px 0;font-size:13px;line-height:1.4}' +
        '.cfg-table td:first-child{color:#5A5856;width:44%}' +
        '.cfg-table td:last-child{font-weight:600}' +
        '.units-section h2{font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#9A9690;font-weight:700;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #E0DDD8}' +
        '.units-table{width:100%;border-collapse:collapse;margin-bottom:14px}' +
        '.units-table th{text-align:left;padding:5px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#9A9690;background:#F9F8F5;border-bottom:1px solid #E0DDD8}' +
        '.units-table td{padding:7px 8px;border-bottom:1px solid #F4EEEC;font-size:13px}' +
        '.units-table .price-cell{font-weight:700;text-align:right}' +
        '.total-bar{background:#1A1A18;color:#fff;padding:14px 18px;border-radius:4px;display:flex;justify-content:space-between;align-items:center;margin-bottom:18px}' +
        '.total-bar .tlabel{font-size:12px;opacity:.7}' +
        '.total-bar .tval{font-size:26px;font-weight:800}' +
        '.footer{border-top:1px solid #E0DDD8;padding-top:10px;display:flex;justify-content:space-between;font-size:10px;color:#9A9690}' +
        '.print-btn{position:fixed;top:20px;right:20px;background:#F24507;color:#fff;border:none;padding:10px 22px;border-radius:4px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 3px 10px rgba(0,0,0,.25);z-index:999}' +
        '.print-btn:hover{background:#D63D06}' +
        '.buy-section{background:#F4EEEC;border-radius:4px;padding:16px 20px;margin-bottom:16px;display:flex;align-items:center;justify-content:space-between;gap:16px}' +
        '.buy-section p{font-size:12px;color:#5A5856;margin:0;line-height:1.4}' +
        '.buy-btn{display:inline-flex;align-items:center;gap:8px;background:#1A1A18;color:#fff;padding:11px 22px;border-radius:3px;text-decoration:none;font-size:14px;font-weight:700;letter-spacing:.02em;white-space:nowrap;flex-shrink:0}' +
        '.buy-btn:hover{background:#F24507}' +
        '.share-link{font-size:10px;color:#9A9690;word-break:break-all;margin-top:4px}' +
        '.pdf-dims-bar{display:flex;gap:0;border:1px solid #E0DDD8;border-radius:4px;overflow:hidden;margin-bottom:16px}' +
        '.pdf-dim{flex:1;padding:10px 12px;border-right:1px solid #E0DDD8}' +
        '.pdf-dim:last-child{border-right:none}' +
        '.pdf-dim-lbl{display:block;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#9A9690;margin-bottom:3px}' +
        '.pdf-dim-val{display:block;font-size:17px;font-weight:800;color:#1A1A18}' +
        '@media print{.print-btn,.buy-btn{display:none!important}.buy-section{background:#F9F8F5;border:1px solid #E0DDD8}.share-link{display:block}.page{padding:0}body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}' +
      '</style></head><body>' +
      '<button class="print-btn" onclick="window.print()">↓ ' + t('pdf_save') + '</button>' +
      '<div class="page">' +
        '<div class="hdr">' +
          (logoUrl ? '<img src="' + logoUrl + '" class="hdr-logo" alt="Logo">' : '<span class="hdr-logo-fallback">AR-Shelving</span>') +
          '<div class="hdr-right"><h1>' + docTitle + '</h1>' +
          '<div class="meta">' + t('spec_sheet_short') + ' &nbsp;·&nbsp; Ref: ' + refStr + ' &nbsp;·&nbsp; ' + dateStr + '</div></div>' +
        '</div>' +
        '<div class="main-grid">' +
          '<img src="' + imgDataUrl + '" class="snapshot" alt="Vista 3D">' +
          '<div class="cfg-section">' +
            '<h2>' + t('pdf_active_config') + '</h2>' +
            '<table class="cfg-table">' +
              '<tr><td>' + t('pdf_altura') + '</td><td>' + cfg.alto + ' cm</td></tr>' +
              '<tr><td>' + t('pdf_anchura') + '</td><td>' + cfg.ancho + ' cm</td></tr>' +
              '<tr><td>' + t('pdf_fondo') + '</td><td>' + cfg.fondo + ' cm</td></tr>' +
              '<tr><td>' + t('pdf_niveles') + '</td><td>' + cfg.niveles + '</td></tr>' +
              '<tr><td>' + t('pdf_tipo_balda') + '</td><td>' + baldaDisplay(cfg.malla) + '</td></tr>' +
              '<tr><td>' + t('pdf_carga') + '</td><td>' + cap + ' kg</td></tr>' +
            '</table>' +
          '</div>' +
        '</div>' +
        '<div class="pdf-dims-bar">' +
          '<div class="pdf-dim"><span class="pdf-dim-lbl">' + t('pdf_dim_alto') + '</span><span class="pdf-dim-val">' + height + ' mm</span></div>' +
          '<div class="pdf-dim"><span class="pdf-dim-lbl">' + t('pdf_dim_ancho_total') + '</span><span class="pdf-dim-val">' + totalWidthMM + ' mm</span></div>' +
          '<div class="pdf-dim"><span class="pdf-dim-lbl">' + t('pdf_dim_fondo') + '</span><span class="pdf-dim-val">' + depthDisplayMM + ' mm</span></div>' +
          '<div class="pdf-dim"><span class="pdf-dim-lbl">' + t('pdf_niveles') + '</span><span class="pdf-dim-val">' + cfg.niveles + '</span></div>' +
          '<div class="pdf-dim"><span class="pdf-dim-lbl">' + t('pdf_carga') + '</span><span class="pdf-dim-val">' + cap + ' kg</span></div>' +
        '</div>' +
        '<div class="units-section"><h2>' + t('pdf_units_title') + '</h2>' +
          '<table class="units-table">' +
            '<thead><tr><th>#</th><th>' + t('pdf_col_module') + '</th><th>' + t('pdf_altura') + '</th><th>' + t('pdf_anchura') + '</th><th>' + t('pdf_fondo') + '</th><th>' + t('pdf_niveles') + '</th><th>' + t('pdf_tipo_balda') + '</th><th>' + t('pdf_carga') + '</th><th style="text-align:right">' + t('price_label') + '</th></tr></thead>' +
            '<tbody>' + unitRowsHtml + '</tbody>' +
          '</table>' +
        '</div>' +
        '<div class="total-bar">' +
          '<div><div class="tlabel">' + units.length + ' ' + (units.length === 1 ? t('pdf_count_one') : t('pdf_count_many')) + '</div></div>' +
          '<div class="tval">' + totalStr + '</div>' +
        '</div>' +
        '<div class="buy-section">' +
          '<div>' +
            '<strong style="font-size:13px;color:#1A1A18">' + t('pdf_buy_title') + '</strong>' +
            '<p>' + t('pdf_buy_text') + '</p>' +
            (shareUrl ? '<div class="share-link">🔗 ' + shareUrl + '</div>' : '') +
          '</div>' +
          (cartUrl
            ? '<a href="' + cartUrl + '" class="buy-btn" target="_blank">' +
                '<svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3z"/></svg>' +
                t('pdf_buy_now') +
              '</a>'
            : '<a href="' + shareUrl + '" class="buy-btn" target="_blank">' + t('pdf_view_config') + '</a>') +
        '</div>' +
        '<div class="footer">' +
          '<span>AR-Shelving · ' + ((window.PalConfiguratorData && window.PalConfiguratorData.consultationEmail) || 'hola@misterregale.com') + ' · www.misterregale.com</span>' +
          '<span>' + docTitle + ' · ' + dateStr + '</span>' +
        '</div>' +
      '</div></body></html>';

    var win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(function() { win.focus(); win.print(); }, 600);
    } else {
      var blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      var url  = URL.createObjectURL(blob);
      var a    = document.createElement('a');
      a.href = url; a.download = 'ficha-tecnica-media-carga.html';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  // ─── UTM helpers ─────────────────────────────────────────────────────────────
  function getUtm() {
    var d = window.PalConfiguratorData;
    var u = (d && d.utm) ? d.utm : {};
    return {
      source:   u.source   || 'configurador-3d',
      medium:   u.medium   || 'referral',
      campaign: u.campaign || 'media-carga',
      content:  u.content  || '',
      term:     u.term     || ''
    };
  }

  function addUtmParams(url) {
    var utm = getUtm();
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var qs  = 'utm_source='   + encodeURIComponent(utm.source)   +
              '&utm_medium='  + encodeURIComponent(utm.medium)   +
              '&utm_campaign='+ encodeURIComponent(utm.campaign);
    if (utm.content) qs += '&utm_content=' + encodeURIComponent(utm.content);
    if (utm.term)    qs += '&utm_term='    + encodeURIComponent(utm.term);
    return url + sep + qs;
  }

  // ─── Share URL ────────────────────────────────────────────────────────────────
  function buildShareUrl() {
    var params = units.map(function (u, i) {
      // Orden del share: alto,ancho,fondo,niveles,modulos,malla,protector,carga,tipoModulo
      return 'u' + i + '=' + [u.config.alto, u.config.ancho, u.config.fondo, u.config.niveles,
                              u.config.modulos || 1,
                              u.config.malla ? 1 : 0, u.config.protector ? 1 : 0,
                              u.config.carga || '1000', unitTypeFor(i)].join(',');
    });
    params.push('active=' + activeUnitIdx);
    if (bundleQty > 1) params.push('q=' + bundleQty);
    var base = window.location.origin + window.location.pathname + '?' + params.join('&');
    return addUtmParams(base);
  }

  function copyShareUrl() {
    var url = buildShareUrl();
    var productTitle = (window.PalConfiguratorData && window.PalConfiguratorData.productTitle) || 'Configuración estantería para palés';

    if (navigator.share) {
      navigator.share({
        title: productTitle,
        text: 'Mira mi configuración personalizada: ',
        url: url
      }).catch(function () { /* usuario canceló */ });
      return;
    }

    showSharePanel(url);
  }

  function showSharePanel(url) {
    var existing = document.getElementById('rc-share-panel');
    if (existing) { existing.remove(); return; }

    var shortUrl = url.length > 55 ? url.substring(0, 52) + '…' : url;
    var waUrl    = 'https://wa.me/?text=' + encodeURIComponent('Mira mi configuración: ' + url);
    var emailUrl = 'mailto:?subject=' + encodeURIComponent('Configuración ' + ((window.PalConfiguratorData && window.PalConfiguratorData.productTitle) || 'Estantería para palés')) +
                   '&body=' + encodeURIComponent('Te comparto mi configuración:\n' + url);

    var panel = document.createElement('div');
    panel.id  = 'rc-share-panel';
    panel.innerHTML =
      '<div class="rc-sp__title">Compartir configuración</div>' +
      '<div class="rc-sp__url">' + shortUrl + '</div>' +
      '<div class="rc-sp__btns">' +
        '<a href="' + waUrl + '" target="_blank" rel="noopener" class="rc-sp__btn rc-sp__btn--wa">' +
          '<svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>' +
          'WhatsApp' +
        '</a>' +
        '<a href="' + emailUrl + '" class="rc-sp__btn rc-sp__btn--email">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' +
          'Email' +
        '</a>' +
        '<button class="rc-sp__btn rc-sp__btn--copy" id="rc-sp-copy">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>' +
          '<span>Copiar enlace</span>' +
        '</button>' +
      '</div>';

    var shareBtn = document.querySelector('.viewer-action-btn:last-child');
    if (shareBtn) {
      var r = shareBtn.getBoundingClientRect();
      panel.style.position = 'fixed';
      panel.style.bottom   = (window.innerHeight - r.top + 10) + 'px';
      panel.style.right    = (window.innerWidth  - r.right + 0) + 'px';
    } else {
      panel.style.position = 'fixed';
      panel.style.bottom   = '90px';
      panel.style.right    = '20px';
    }

    document.body.appendChild(panel);

    var copyBtn = document.getElementById('rc-sp-copy');
    function doCopy() {
      function done() {
        copyBtn.querySelector('span').textContent = '✓ Copiado';
        copyBtn.style.background = '#22a06b';
        setTimeout(function () { panel.remove(); }, 1400);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done).catch(function () {
          var ta = document.createElement('textarea');
          ta.value = url; ta.style.cssText = 'position:fixed;opacity:0';
          document.body.appendChild(ta); ta.focus(); ta.select();
          try { document.execCommand('copy'); } catch(e){}
          document.body.removeChild(ta); done();
        });
      } else {
        var ta = document.createElement('textarea');
        ta.value = url; ta.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { document.execCommand('copy'); } catch(e){}
        document.body.removeChild(ta); done();
      }
    }
    copyBtn.addEventListener('click', doCopy);

    setTimeout(function () {
      document.addEventListener('click', function closePanel(e) {
        if (!panel.contains(e.target)) {
          panel.remove();
          document.removeEventListener('click', closePanel);
        }
      });
    }, 50);
  }

  function showToast(msg) {
    var toast = document.getElementById('config-toast');
    if (!toast) {
      toast = document.createElement('div'); toast.id = 'config-toast'; toast.className = 'config-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg; toast.classList.add('visible');
    clearTimeout(toast._hideTimeout);
    toast._hideTimeout = setTimeout(function () { toast.classList.remove('visible'); }, 2500);
  }

  // ─── Pre-configurar desde el producto aterrizado ────────────────────────────
  // SOLO por SKU. Ver el comentario de dentro: en esta gama no hay respaldo por handle.
  function applyProductConfig() {
    var data = window.PalConfiguratorData;
    // AUTORITATIVO Y PRIMERO: config + tipo de módulo EXACTOS desde el SKU del producto
    // (language-neutral). Imprescindible fuera de ES: el handle alemán ("weitspannregal…")
    // no contiene 'carga-pesada', así que el guard de abajo devolvía false y la config caía
    // al default (mismo producto de inicio para todos). El SKU no se traduce.
    // ⚠️ En esta gama el SKU es la ÚNICA vía. No hay respaldo por handle, a diferencia de
    // las otras cinco: el mercado ES está en DRAFT, no se publica hreflang="es" y los
    // handles están traducidos por mercado, así que no hay ningún patrón de texto estable
    // que parsear. Si el SKU no viene o no casa, se arranca con la config por defecto —
    // que es un fallo visible y acotado, no un producto equivocado.
    var _skuCfg = configFromPalSku(currentProductSku());
    if (!_skuCfg) {
      console.warn('[PAL] sin SKU utilizable en la ficha; se arranca con la config por defecto');
      return false;
    }
    units[0].tipoModulo = _skuCfg.tipoModulo;
    forceAdditionalView = (_skuCfg.tipoModulo === 'adicional');
    var _c0 = units[0].config;
    _c0.alto      = _skuCfg.alto;
    _c0.ancho     = _skuCfg.ancho;
    _c0.fondo     = _skuCfg.fondo;
    _c0.niveles   = _skuCfg.niveles;
    _c0.modulos   = _skuCfg.modulos;
    _c0.malla     = _skuCfg.malla;
    _c0.protector = _skuCfg.protector;
    _c0.carga     = _skuCfg.carga;

    // Congelar la config del producto ATERRIZADO: es la única contra la que
    // option_groups_data puede resolver un salto de un eje.
    cfgAterrizada = Object.assign({}, _c0);
    cfgAterrizada._unitType = _skuCfg.tipoModulo;

    // Aviso si el metacampo no describe el mismo producto que el SKU: significa que están
    // desincronizados y el atajo resolvería mal. Se detecta comparando el valor ACTIVO de
    // cada eje con el que dice el SKU.
    if (ogSubgrupos()) {
      var desajuste = OG_EJES.filter(function (d) {
        var act = ogActivo(d.eje);
        return act !== null && act !== d.valor(_c0);
      }).map(function (d) { return d.eje + ': metacampo="' + ogActivo(d.eje) + '" sku="' + d.valor(_c0) + '"'; });
      if (desajuste.length) {
        console.warn('[PAL] option_groups_data no cuadra con el SKU, se ignora el atajo: ' + desajuste.join(' · '));
        cfgAterrizada = null;
      }
    }
    return true;
  }

  // ─── Leer config desde URL params ────────────────────────────────────────────
  function readUrlConfig() {
    if (!window.location.search) return false;
    var params; try { params = new URLSearchParams(window.location.search); } catch (e) { return false; }
    var loaded = []; var i = 0;
    while (params.has('u' + i)) {
      // Orden: alto,ancho,fondo,niveles,modulos,malla,protector,carga,tipoModulo
      var parts = params.get('u' + i).split(',');
      if (parts.length < 4) break;
      var alto = parts[0], ancho = parts[1], fondo = parts[2];
      var niveles = parseInt(parts[3], 10) || 2;
      var modulos = parseInt(parts[4], 10) || 1;
      var malla     = (parts[5] === '1' || parts[5] === 'si');
      var protector = (parts[6] === '1' || parts[6] === 'si');
      var carga     = (parts[7] === '500') ? '500' : '1000';
      var tipoMod = (parts[8] === 'adicional') ? 'adicional' : (parts[8] === 'inicial' ? 'inicial' : null);
      if (!PRODUCT_DIMS.frameHeights[alto]) alto = '300';
      if (!PRODUCT_DIMS.widths[ancho]) { i++; continue; }
      fondo = FONDO_FIJO;                       // toda la gama comparte fondo
      // El recorte altura↔niveles se aplica también aquí: un enlace compartido con una
      // combinación imposible se sanea al nivel válido más bajo de esa altura.
      var validN = nivelesValidos(alto);
      if (!validN.length) validN = [2];
      if (validN.indexOf(niveles) === -1) niveles = validN[0];
      if (modulos < 1 || modulos > 4) modulos = 1;
      // Si el share no traía tipoModulo, la 1ª es inicial y el resto adicionales.
      if (!tipoMod) tipoMod = (i === 0) ? 'inicial' : 'adicional';
      loaded.push({ id: i + 1, tipoModulo: tipoMod,
                    config: { alto: alto, ancho: ancho, fondo: fondo, niveles: niveles,
                              modulos: modulos, malla: malla, protector: protector, carga: carga },
                    model: null, xOffset: 0 });
      i++;
    }
    if (!loaded.length) return false;
    // Restricción de gama: todos los módulos del tramo comparten fondo (que además es fijo)
    loaded.forEach(function (u) { u.config.fondo = FONDO_FIJO; });
    units = loaded;
    activeUnitIdx = Math.min(parseInt(params.get('active') || '0', 10), units.length - 1);
    var q = parseInt(params.get('q') || '1', 10);
    bundleQty = (isNaN(q) || q < 1) ? 1 : Math.min(q, MAX_BUNDLES);
    normalizeUnitTypes();   // saneo por si el enlace trae un adicional inválido
    return true;
  }

  // ─── Botones de accion: ficha técnica + share ─────────────────────────────────
  function addActionButtons() {
    var consultBtn = document.querySelector('.consultation-btn');
    if (!consultBtn) return;
    var bar = document.createElement('div'); bar.className = 'viewer-action-bar';

    var screenshotBtn = document.createElement('button'); screenshotBtn.className = 'viewer-action-btn';
    screenshotBtn.innerHTML = '<svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 10L3 6h2.5V2h3v4H11L7 10ZM2 12v-1.5h10V12H2Z" fill="currentColor"/></svg> ' + t('spec_sheet');
    screenshotBtn.title = t('spec_sheet_title');
    screenshotBtn.addEventListener('click', captureScreenshot);

    var shareBtn = document.createElement('button'); shareBtn.className = 'viewer-action-btn';
    shareBtn.innerHTML = '<svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M10.5 9.5a1.5 1.5 0 0 0-1.18.58L5.14 7.8a1.55 1.55 0 0 0 0-.6l4.18-2.28A1.5 1.5 0 1 0 8.75 3.5c0 .14.03.27.07.4L4.64 6.18a1.5 1.5 0 1 0 0 1.64l4.18 2.28a1.49 1.49 0 0 0-.07.4A1.5 1.5 0 1 0 10.5 9.5Z" fill="currentColor"/></svg> ' + t('share');
    shareBtn.title = t('share');
    shareBtn.addEventListener('click', copyShareUrl);

    bar.appendChild(screenshotBtn); bar.appendChild(shareBtn);
    consultBtn.insertAdjacentElement('afterend', bar);
  }

  // ─── Tabs de unidades ────────────────────────────────────────────────────────
  function renderUnitTabs() {
    // Las unidades se seleccionan/eliminan desde las filas de precio (clicables).
    // Aquí solo se renderiza el botón "+ Añadir producto" al final de las opciones.
    var addRow = document.getElementById('unit-add-row');
    if (!addRow) return;
    addRow.innerHTML = '';
    if (units.length < MAX_UNITS) {
      var addBtn = document.createElement('button');
      addBtn.className = 'unit-tab unit-tab--add';
      addBtn.type = 'button';
      addBtn.title = t('add_unit');
      addBtn.textContent = t('add_unit');
      addBtn.addEventListener('click', function () { addUnit(); });
      addRow.appendChild(addBtn);
    }
  }

    function setActiveUnit(idx) {
    activeUnitIdx = idx; renderUnitTabs(); updateUnitHighlight(); syncPanelToUnit();
    syncQtyInput(); syncModulesInput(); updatePriceAndCart(); updateSpecs(); createDimLines();
    var selUnit = units[idx];
    if (selUnit && selUnit.model) {
      var selBox = visibleBox(selUnit.model);
      var selCenter = selBox.getCenter(new THREE.Vector3());
      var selSize = selBox.getSize(new THREE.Vector3());
      var camDist = Math.max(selSize.x, selSize.y, selSize.z) * 2.2;
      animateCameraTo(
        new THREE.Vector3(selCenter.x + camDist*0.5, selCenter.y + camDist*0.6, selCenter.z + camDist*0.9),
        selCenter, 450
      );
    }
  }

  function addUnit() {
    if (units.length >= MAX_UNITS) return;
    var active = activeUnit();
    var defCfg = Object.assign({}, active.config);
    // Hereda el tipo de la unidad activa (modo actual): si estás en "inicial",
    // añade otra inicial; si estás en "adicional", añade una extensión.
    var defTipo = unitTypeFor(activeUnitIdx);
    units.push({ id: units.length + 1, tipoModulo: defTipo, config: defCfg, model: null, xOffset: 0 });
    normalizeUnitTypes();
    pushHistory(); refreshUnitsNoCamera(); adjustCameraToAllUnits();
    renderUnitTabs(); setActiveUnit(units.length - 1);
  }

  function removeUnit(idx) {
    if (units.length <= 1) return;
    if (units[idx].model) { scene.remove(units[idx].model); disposeModel(units[idx].model); units[idx].model = null; }
    units.splice(idx, 1);
    if (activeUnitIdx >= units.length) activeUnitIdx = units.length - 1;
    normalizeUnitTypes();   // el vecino izq. de un adicional puede haber cambiado al borrar
    pushHistory(); refreshUnitsNoCamera(); adjustCameraToAllUnits();
    renderUnitTabs(); syncPanelToUnit();
  }

  function syncPanelToUnit() {
    var cfg = activeUnit().config;
    document.querySelectorAll('[data-alto]').forEach(function (btn)    { btn.classList.toggle('active', btn.dataset.alto === cfg.alto); });
    document.querySelectorAll('[data-ancho]').forEach(function (btn)   { btn.classList.toggle('active', btn.dataset.ancho === cfg.ancho); });
    document.querySelectorAll('[data-fondo]').forEach(function (btn)   { btn.classList.toggle('active', btn.dataset.fondo === cfg.fondo); });
    document.querySelectorAll('[data-niveles]').forEach(function (btn) { btn.classList.toggle('active', parseInt(btn.dataset.niveles, 10) === cfg.niveles); });
    document.querySelectorAll('[data-malla]').forEach(function (btn) { btn.classList.toggle('active', btn.dataset.malla === palAcabado(cfg)); });
    // Selector Inicial/Adicional → reflejar el tipo de la unidad activa
    var tm = unitTypeFor(activeUnitIdx);
    var tmLabelEl = document.getElementById('label-tipo-modulo');
    document.querySelectorAll('[data-tipo-modulo]').forEach(function (btn) {
      var on = (btn.dataset.tipoModulo === tm);
      btn.classList.toggle('active', on);
      if (on && tmLabelEl) tmLabelEl.textContent = btn.textContent.trim();
    });
    // El adicional solo existe con pack de 1: bloquear 2-4 aquí también, para que el estado
    // sea correcto al arrancar y al cambiar de módulo activo, no solo al pulsar el selector.
    aplicaLimiteModulos(tm);
    // RECORTE ESTRUCTURAL: a cada altura solo existen ciertos niveles, así que los demás
    // se OCULTAN (no se grisan). Es determinista y no cuesta ni una petición.
    var validN = nivelesValidos(cfg.alto);
    document.querySelectorAll('[data-niveles]').forEach(function (btn) {
      var n = parseInt(btn.dataset.niveles, 10);
      btn.style.display = validN.indexOf(n) >= 0 ? '' : 'none';
    });
    // Ejes booleanos y de valor propios de esta gama
    document.querySelectorAll('[data-malla]').forEach(function (btn) {
      btn.classList.toggle('active', (btn.dataset.malla === 'si') === !!cfg.malla);
    });
    document.querySelectorAll('[data-protector]').forEach(function (btn) {
      btn.classList.toggle('active', (btn.dataset.protector === 'si') === !!cfg.protector);
    });
    document.querySelectorAll('[data-carga]').forEach(function (btn) {
      btn.classList.toggle('active', String(btn.dataset.carga) === String(cfg.carga));
    });
    document.querySelectorAll('[data-modulos]').forEach(function (btn) {
      btn.classList.toggle('active', parseInt(btn.dataset.modulos, 10) === parseInt(cfg.modulos, 10));
    });
  }

  // Marca como NO SELECCIONABLES (visibles pero bloqueadas, .opt-unavailable) las
  // opciones cuya combinación resultante está agotada. Disponibilidad en vivo vía
  // product.json (cacheada en priceCache). FAIL-OPEN: si no se conoce todavía
  // (fetch en curso, fallo de red o sin backend), NO se bloquea — el botón de
  // añadir es la red de seguridad. Solo se bloquea lo que se SABE agotado.
  var _availGen = 0;
  function updateOptionAvailability() {
    var gen = ++_availGen;
    var cfg = activeUnit().config;
    var uType = unitTypeFor(activeUnitIdx);
    function apply(btn, ok) {
      if (gen !== _availGen) return;                   // la config cambió: callback obsoleto
      if (btn.classList.contains('active')) ok = true; // el valor activo nunca se bloquea
      btn.classList.toggle('opt-unavailable', !ok);
      if (ok) btn.removeAttribute('aria-disabled'); else btn.setAttribute('aria-disabled', 'true');
    }
    // ATAJO SIN RED: mientras el usuario siga en el producto aterrizado, el metacampo
    // option_groups_data ya dice qué opciones existen — las que no, vienen con handle
    // null. Es autoritativo y no cuesta ni una petición. En cuanto se mueve de ese
    // producto deja de aplicar (el metacampo describe SOLO el producto de la página).
    var enAterrizada = !!cfgAterrizada && OG_EJES.every(function (d) {
      return d.valor(cfg) === d.valor(cfgAterrizada);
    });
    function ogDescarta(cand) {
      if (!enAterrizada) return false;
      var dif = OG_EJES.filter(function (d) { return d.valor(cand) !== d.valor(cfg); });
      if (dif.length !== 1) return false;
      var validas = ogEtiquetasValidas(dif[0].eje);
      return !!validas && validas.indexOf(dif[0].valor(cand)) === -1;
    }

    function check(cand, ut, btn) {
      // Huecos DETERMINISTAS del catálogo (carga 500 + pack de 1). Van antes que todo: el
      // camino de red es fail-open y un producto inexistente no responde, así que por ahí no
      // se grisaría nunca y el usuario caería en una combinación sin precio.
      if (!combinacionExiste(cand)) { apply(btn, false); return; }
      if (ogDescarta(cand)) { apply(btn, false); return; }
      var cached = priceCache[mappingKey(cand, ut)];
      if (cached) { apply(btn, cached.available !== false); return; }
      // Esta gama no tiene mercado ES publicado, así que no hay ningún camino "directo" más
      // barato: el grisado por red se comporta igual en todos los mercados.
      // El recorte altura↔niveles NO llega hasta aquí: se resuelve antes con la tabla
      // ALTURA_NIVELES, que es determinista y no gasta ni una petición.
      // FAIL-OPEN: solo se grisa si el .js RESPONDE con available:false; si falla → no grisar.
      getCachedOrFetch(cand, ut, function (info) { apply(btn, !info || info.available !== false); });
    }
    // Cada rama CLONA cfg: es units[i].config, una referencia viva que los click handlers
    // mutan in situ, y check() la lee después de esperas de red.
    document.querySelectorAll('[data-alto]').forEach(function (b) {
      var c = Object.assign({}, cfg); c.alto = b.dataset.alto;
      // Al cambiar de altura los niveles actuales pueden no existir: se prueba con el
      // nivel válido de esa altura, no con el actual, para no grisar en falso. Y con el
      // MISMO nivel que elegirá el click (nivelExistente), no siempre con el primero: si no,
      // un hueco en el primer nivel grisaba la altura entera aunque otro nivel sí existiera.
      var vn = nivelesValidos(c.alto);
      if (vn.length && vn.indexOf(parseInt(c.niveles, 10)) === -1) c.niveles = nivelExistente(c, vn);
      check(c, uType, b);
    });
    document.querySelectorAll('[data-ancho]').forEach(function (b) { var c = Object.assign({}, cfg); c.ancho = b.dataset.ancho; check(c, uType, b); });
    document.querySelectorAll('[data-niveles]').forEach(function (b) { if (b.style.display === 'none') return; var c = Object.assign({}, cfg); c.niveles = parseInt(b.dataset.niveles, 10); check(c, uType, b); });
    document.querySelectorAll('[data-malla]').forEach(function (b) { var c = Object.assign({}, cfg); c.malla = (b.dataset.malla === 'si'); check(c, uType, b); });
    document.querySelectorAll('[data-protector]').forEach(function (b) { var c = Object.assign({}, cfg); c.protector = (b.dataset.protector === 'si'); check(c, uType, b); });
    document.querySelectorAll('[data-carga]').forEach(function (b) { var c = Object.assign({}, cfg); c.carga = b.dataset.carga; check(c, uType, b); });
    document.querySelectorAll('[data-modulos]').forEach(function (b) {
      // El adicional solo existe con pack de 1 (familias 2 y 4 del catálogo). Se resuelve
      // AQUÍ, y no con un toggle aparte, porque esta función hace `toggle` sobre la misma
      // clase y borraría cualquier grisado puesto por fuera.
      if (parseInt(b.dataset.modulos, 10) > 1 && uType === 'adicional') { apply(b, false); return; }
      var c = Object.assign({}, cfg); c.modulos = parseInt(b.dataset.modulos, 10); check(c, uType, b);
    });
    // Clonar como las 5 líneas de arriba: `cfg` es units[i].config (referencia viva que los
    // click handlers mutan in situ) y check() la lee tras esperas de red.
    document.querySelectorAll('[data-tipo-modulo]').forEach(function (b) { check(Object.assign({}, cfg), b.dataset.tipoModulo, b); });
  }

  function getFrameHeight(cfg) {
    return PRODUCT_DIMS.frameHeights[String(cfg.alto)] || 3000;
  }

  function updateSpecs() {
    var cfg = activeUnit().config; var el;
    el = document.getElementById('spec-height');   if (el) el.textContent = getFrameHeight(cfg) + ' mm';
    // El ancho que se enseña es el del PACK completo: N módulos de luz libre.
    el = document.getElementById('spec-width');
    if (el) el.textContent = ((PRODUCT_DIMS.widths[cfg.ancho] || 0) * (parseInt(cfg.modulos, 10) || 1)) + ' mm';
    el = document.getElementById('spec-depth');    if (el) el.textContent = (PRODUCT_DIMS.depths[String(cfg.fondo)] || 1100) + ' mm';
    // ⚠️ La carga de esta gama es POR PALÉ, no por balda. El metacampo
    // capacidad_de_carga_por_balda_kg NO se usa: está en 64 de 616 y es incoherente.
    el = document.getElementById('spec-capacity');
    if (el) el.textContent = cargaPorPale(cfg) + ' kg/' + (t('spec_carga_pale') || 'palé').split('/').pop().trim();
    el = document.getElementById('spec-pales');
    if (el) el.textContent = String(palesTotales(cfg));
    renderSpecs();
    updateLabels(); updateSummary();
    if (window._updateConsultationHref) window._updateConsultationHref();
    updateTechCard();
  }

  // Tabla "Especificaciones" (full-width, bajo la descripción) — se adapta a la
  // config activa. Las etiquetas las pinta el Liquid (localizadas); aquí solo los
  // valores, por id. set() es no-op si la fila no existe (rows distintas por gama).
  function renderSpecs() {
    // Las filas viven dentro de la pestaña #tab-specs (o el bloque legacy #rc-specs);
    // las poblamos siempre por id. Si existe la sección product-tabs del TEMA (no la
    // nuestra) con specs estáticas y además nuestro #rc-specs legacy, evitamos duplicar.
    var legacy = document.getElementById('rc-specs');
    if (legacy && document.getElementById('tab-specs') && !legacy.querySelector('#spec-row-niveles')) {
      legacy.style.display = 'none';
    }
    var cfg = activeUnit().config;
    function set(id, val) { var el = document.getElementById(id); if (el) el.textContent = val; }
    set('spec-row-niveles',     cfg.niveles);
    set('spec-row-altura',      cfg.alto + ' cm');
    set('spec-row-anchura',     cfg.ancho + ' cm');
    set('spec-row-profundidad', cfg.fondo + ' cm');
    set('spec-row-tipo',        baldaDisplay(cfg.malla));
    set('spec-row-carga',       cargaPorPale(cfg) + ' kg');
    set('spec-row-modulos',     String(cfg.modulos || 1));
    set('spec-row-protector',   siNo(cfg.protector));
    set('spec-row-pales',       String(palesTotales(cfg)));
  }

  function updateLabels() {
    var cfg = activeUnit().config; var el;
    var baldaLabel = baldaDisplay(cfg.malla);
    el = document.getElementById('label-alto');       if (el) el.textContent = cfg.alto + ' cm';
    el = document.getElementById('label-ancho');      if (el) el.textContent = cfg.ancho + ' cm';
    el = document.getElementById('label-fondo');      if (el) el.textContent = cfg.fondo + ' cm';
    el = document.getElementById('label-niveles');    if (el) el.textContent = cfg.niveles + ' ' + t('niveles_word');
    el = document.getElementById('label-tipo-balda'); if (el) el.textContent = baldaLabel;
  }

  function updateSummary() {
    var el = document.getElementById('config-summary'); if (!el) return;
    var cfg = activeUnit().config;
    var tipoLbl = baldaDisplay(cfg.malla);
    var nivWord = t('niveles_word');
    el.textContent = t('summary_prefix') + ' · ' + cfg.alto + 'x' + cfg.ancho + 'x' + cfg.fondo + ' cm · ' + cfg.niveles + ' ' + nivWord + ' · ' + tipoLbl;
  }

  // ─── Contenido dinámico: título + descripción según config ─────────────────────
  // Título calculado desde la config (fallback cuando no hay título de producto real).
  function computedTitle(cfg) {
    var data = window.PalConfiguratorData || {};
    var base = (t('summary_prefix') || data.titleBase || data.productTitle || 'Estantería').trim();
    var H = cfg.alto, W = cfg.ancho, D = cfg.fondo, N = cfg.niveles;
    var tipoLbl = baldaDisplay(cfg.malla);
    var nivWord = t('niveles_word') || 'niveles';
    var dims = H + '×' + W + '×' + D + ' cm';
    var parts = [base, dims, N + ' ' + nivWord];
    if (tipoLbl) parts.push(tipoLbl);
    return parts.join(' · ');
  }

  // ─── Contenido localizado ────────────────────────────────────────────────────
  // Las otras cinco gamas tienen aquí un `resolveLocalizedContent()` que pide el HTML del
  // producto ES, lee su <link hreflang> hacia el handle traducido del mercado y hace un
  // segundo fetch para sacar título y descripción localizados.
  //
  // ⚠️ En esta gama ESO NO EXISTE, y no por simplificar: se apoya en `/es-es/products/…`,
  // y el mercado ES está en DRAFT (302 a /de-de/, sin hreflang="es"). No hace falta además:
  // la resolución por SKU ya se hace en el root del mercado del visitante, así que el `.js`
  // devuelve el título y la descripción YA localizados y buildInfo los guarda en priceCache.
  // Si alguien clona esta gama a otra, ojo: la función que falta aquí no es un olvido.

  // H1 y descripción con el contenido localizado real del producto resuelto.
  //
  // ⚠️ Mucho más simple que en las otras cinco gamas, y por la misma razón de fondo: allí
  // hay que partir del handle ES y seguir su <link hreflang> para encontrar el producto
  // traducido. Aquí NO existe handle ES (mercado en DRAFT), pero tampoco hace falta: como
  // la resolución por SKU ya se hace en el ROOT DEL MERCADO DEL VISITANTE, el `.js` que
  // devuelve trae el título y la descripción YA localizados, y buildInfo los guarda.
  function applyLocalizedContent() {
    var data = window.PalConfiguratorData || {};
    var key = mappingKey(activeUnit().config, unitTypeFor(activeUnitIdx));
    var loc = priceCache[key];
    if (loc && (loc.title || loc.description)) {
      var fixedTitle = (data.sectionTitle != null ? String(data.sectionTitle) : '').trim();
      if (!fixedTitle && loc.title) {
        document.querySelectorAll('.rc-prod-title, .product-title').forEach(function (el) { el.textContent = loc.title; });
        // NO tocar document.title: el <title>/meta SEO del <head> los sirve Shopify por PRODUCTO
        // (cada config es su propia URL) y deben quedar FIJOS por URL aunque se reconfigure
        // (ads/SEO/redes). Solo se actualiza el H1 visible del configurador.
      }
      if (data.showDescription && loc.description && loc.description.trim()) {
        var themeRte = document.querySelector('#tab-description .rte');
        if (themeRte) { themeRte.innerHTML = loc.description; }
        else {
          var sec = document.getElementById('rc-description');
          var body = document.getElementById('rc-description-body');
          if (sec && body) { body.innerHTML = loc.description; sec.style.display = ''; }
        }
      }
    }
  }

  // Actualiza el H1 (solo si el título de sección está vacío) y la sección de
  // descripción con los datos del producto resuelto por la config activa.
  function renderProductContent(info) {
    var data = window.PalConfiguratorData || {};

    // Título: respeta un título fijo definido en los ajustes de sección.
    var isPrimary = !(window.Shopify && window.Shopify.locale) || String(window.Shopify.locale).toLowerCase().slice(0, 2) === 'es';
    var fixedTitle = (data.sectionTitle != null ? String(data.sectionTitle) : '').trim();
    if (!fixedTitle) {
      // Usamos el título REAL del producto resuelto en el MERCADO ACTUAL (info.title viene
      // del .js local, p.ej. "Weitspannregal…" en DE, "Schraubregale…" en Versatile). NO se
      // autotraduce ni se usa un nombre genérico. computedTitle solo como último recurso.
      var newTitle = (info && info.title) ? info.title : computedTitle(activeUnit().config);
      if (newTitle) {
        document.querySelectorAll('.rc-prod-title, .product-title').forEach(function (el) {
          el.textContent = newTitle;
        });
        // NO tocar document.title (ver nota en applyLocalizedContent): el <title>/meta SEO se
        // mantienen por URL, no cambian al seleccionar otra config.
      }
    }

    // Descripción (full-width, toggle por ajuste de sección).
    if (data.showDescription) {
      var sec = document.getElementById('rc-description');
      // Descripción del producto RESUELTO por la config activa (info.description = HTML del .js
      // local). SIN guard isPrimary: antes en no-ES se vaciaba y la descripción quedaba
      // congelada en el producto aterrizado en vez de reflejar la config seleccionada.
      var fetched = (info && info.description) ? info.description : '';
      var themeRte = document.querySelector('#tab-description .rte');
      if (themeRte) {
        // El tema muestra la descripción (product-tabs): la actualizamos con la del
        // producto resuelto por config (fetch) para que se adapte; ocultamos la del configurador.
        if (fetched && fetched.trim()) themeRte.innerHTML = fetched;
        if (sec) sec.style.display = 'none';
      } else if (sec) {
        var body = document.getElementById('rc-description-body');
        if (isPrimary) {
          var html = fetched || (data.productDescription || '');
          if (body) {
            if (html && html.trim()) { body.innerHTML = html; sec.style.display = ''; }
            else { sec.style.display = 'none'; }
          }
        } else if (body) {
          sec.style.display = (body.textContent && body.textContent.trim()) ? '' : 'none';
        }
      }
    }

    // NO-ES: mejora asíncrona a título + descripción localizados del producto resuelto.
    applyLocalizedContent();
  }

  // ─── IVA: respeta el toggle de la app master-custom (vat-price-toggle) ─────────
  // variant.price viene BRUTO (IVA incluido, tienda ES). En modo 'excl' (sin IVA)
  // se muestra neto = bruto/(1+rate/100). El configurador calcula el precio en el
  // modo activo; sus contenedores van renombrados para que la app NO los toque
  // (la app convierte solo el primer número → descuadra ofertas/multi-unidad).
  // ─── Moneda: el .js siempre da el precio en la moneda BASE de la tienda (EUR).
  // Cada mercado tiene su moneda de presentación (Shopify.currency.active) y su tasa
  // (Shopify.currency.rate). Convertimos en origen para que todos los displays salgan
  // en la moneda del país (PLN, CZK, HUF, EUR…). En standalone (sin Shopify) → EUR x1.
  function curRate() { try { var r = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1'); return (r > 0) ? r : 1; } catch (e) { return 1; } }
  function curCode() { try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'; } catch (e) { return 'EUR'; } }
  // El .js (fetch al mercado actual) YA devuelve el precio en la moneda de presentación
  // del país; NO reconvertir por tasa (eso duplicaría). Passthrough; solo etiqueta curCode.
  function toPresentment(cents) { return cents || 0; }

  // Modo IVA del toggle master-custom. Tienda con precios NETOS (taxesIncluded=false):
  // 'excl' (sin IVA) = precio base tal cual; 'incl' (con IVA) = base + IVA del mercado.
  //
  // OJO: el defecto NO es 'excl'. El app solo escribe localStorage['mc-vat-mode'] cuando el
  // usuario TOCA el toggle; mientras no lo haya tocado — el caso de toda visita nueva — manda
  // el modo que declara el propio app en #mc-vat-toggle[data-default-mode], que hoy es 'incl'.
  // Asumir 'excl' hacía que el configurador mostrase el NETO mientras el resto de la tienda
  // mostraba el BRUTO del mismo producto (medido en live, DE: 125,97 EUR en el configurador
  // contra 149,90 EUR en el listado). Se LEE del DOM en vez de fijarlo aquí para que siga
  // cuadrando si algún día cambian ese ajuste del app.
  //
  // Y el ÚLTIMO respaldo también es 'incl', no 'excl': si el toggle no está en la página
  // (pasa en el tema DEV, que no lleva ese app) hay que enseñar igualmente el precio CON IVA,
  // porque es el que enseña la tienda. Con 'excl' el configurador de dev mostraba el neto
  // (134,37 EUR) del producto que en la tienda vale el importe redondo (159,90 EUR), así que la
  // revisión en dev no se parecía a producción.
  function vatDefaultMode() {
    try {
      var el = document.getElementById('mc-vat-toggle') || document.querySelector('[data-mc-vat-root]');
      var m = el && String(el.getAttribute('data-default-mode') || '').toLowerCase();
      if (m === 'incl' || m === 'excl') return m;
    } catch (e) {}
    return 'incl'; // sin toggle en la página: con IVA, igual que la tienda
  }
  function vatMode() {
    try { var v = localStorage.getItem('mc-vat-mode'); if (v === 'incl' || v === 'excl') return v; } catch (e) {}
    return vatDefaultMode();
  }
  // IVA del PAÍS del mercado actual. Fuente PRINCIPAL: la tasa del tema
  // (window.Theme.settings.vat_rate, del snippet vat-rate.liquid) → cuadra al céntimo con
  // ficha/listados/carrito. Respaldos: mapa por país (Shopify.country) y localStorage (solo
  // si su país coincide). El .mc-total-tax del app NO es fiable (marca 21% en todo mercado),
  // y tampoco sirve el data-vat-rate del bloque del app: es su ajuste GLOBAL (21) y no cambia
  // por mercado — en DE marcaría 21% en vez de 19%.
  function vatRate() {
    try { var tr = Number(window.Theme && window.Theme.settings ? window.Theme.settings.vat_rate : NaN); if (isFinite(tr) && tr > 0 && tr < 100) return tr; } catch (e) {}
    var country = '';
    try { country = ((window.Shopify && Shopify.country) || '').toUpperCase(); } catch (e) {}
    var MAP = { ES: 21, DE: 19, PT: 23, PL: 23, CZ: 21, SK: 23, HU: 27, FR: 20, IT: 22, AT: 20, NL: 21, BE: 21, IE: 23, RO: 19, GR: 24, DK: 25, SE: 25, FI: 25.5, LU: 17 };
    if (country && MAP[country] != null) return MAP[country];
    try {
      var p = (localStorage.getItem('mc-vat-rate-v2') || '').split('|');
      if (p[0] && country && p[0].toUpperCase() === country) { var r = parseFloat(p[1]); if (r > 0 && r < 100) return r; }
    } catch (e) {}
    return 21;
  }
  // Precio base NETO → 'incl' suma el IVA del mercado; 'excl' lo deja tal cual.
  function vatCents(c) { return (vatMode() === 'incl') ? Math.round(c * (1 + vatRate() / 100)) : c; }

  // Nota de IVA bajo el precio — refleja el modo del toggle master-custom (incl. con
  // su % / excl.). Se re-ejecuta en cada updatePriceAndCart (el toggle ya lo dispara).
  function updateVatNote() {
    var el = document.getElementById('variant-price-net'); if (!el) return;
    var txt;
    if (vatMode() === 'excl') {
      txt = t('vat_excl');
    } else {
      txt = String(t('vat_incl') || '').replace('{rate}', vatRate());
    }
    if (txt) el.textContent = txt;
  }

  // Calcula precio del CONJUNTO (suma de unidades del run, cada una con SU tipo) y
  // lo multiplica por bundleQty. Resumen compacto: módulos una vez + '× N conjuntos'.
  var _priceGen = 0;
  function updatePriceAndCart() {
    // Token de generación (mismo patrón que _availGen): una llamada MÁS RECIENTE invalida los
    // callbacks de red de las anteriores todavía en curso, para que un fetch lento (p.ej. el
    // fallback viaHreflang) no pise el DOM con la config vieja si el usuario ya reconfiguró.
    // Debe ser un primitivo capturado SÍNCRONAMENTE al entrar.
    var gen = ++_priceGen;
    var data     = window.PalConfiguratorData;
    var currency = curCode();
    var priceEl  = document.getElementById('variant-price');
    var cartBtn  = document.getElementById('add-to-cart-btn');

    if (priceEl) priceEl.innerHTML = '<span class="rc-price-calculating">' + t('price_calc') + '</span>';
    if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = t('price_calc'); cartBtn.className = 'add-to-cart-btn'; }

    function fmt(cents) { cents = vatCents(cents); return (cents / 100).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' ' + currency; }

    function bindRowRemoves() {
      if (!priceEl) return;
      priceEl.querySelectorAll('[data-remove-unit]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.stopPropagation();
          removeUnit(parseInt(b.getAttribute('data-remove-unit'), 10));
        });
      });
      priceEl.querySelectorAll('.unit-price-row[data-unit-idx]').forEach(function (row) {
        row.addEventListener('click', function (e) {
          if (e.target.classList.contains('upr-remove')) return;
          setActiveUnit(parseInt(row.dataset.unitIdx, 10));
        });
      });
    }

    var results = new Array(units.length);
    var pending = units.length;
    units.forEach(function (u, i) {
      getCachedOrFetch(u.config, unitTypeFor(i), function (info) {
        results[i] = info;
        if (--pending > 0) return;
        render();
      }, true);
    });

    function render() {
      if (gen !== _priceGen) return; // config ya cambió mientras estos fetches estaban en curso
      var multiUnit   = units.length > 1;
      // OJO: "no resuelto" (ri == null) y "agotado" (ri.available === false) son estados
      // DISTINTOS y no deben mezclarse: si no encontramos el producto no podemos afirmar que
      // esté sin stock (era la causa del falso "Sin stock", y del badge rojo, al reconfigurar).
      var bundleCents = 0, allAvail = true, anyPrice = false, anyUnresolved = false;
      for (var k = 0; k < units.length; k++) {
        var ri = results[k];
        if (!ri || !ri.price) { anyUnresolved = true; continue; }
        anyPrice = true; bundleCents += ri.price;
        if (!ri.available) allAvail = false;
      }
      var grandCents = bundleCents * bundleQty;
      var html = '';

      if (multiUnit) {
        for (var k2 = 0; k2 < units.length; k2++) {
          var ui = units[k2], r2 = results[k2];
          var tLbl = unitTypeFor(k2) === 'inicial' ? t('pdf_mod_inicial') : t('pdf_mod_adicional');
          var bLbl = baldaDisplay(ui.config.malla);
          var val  = (r2 && r2.price) ? fmt(r2.price) : '—';
          var cmp  = (r2 && r2.compareAt && r2.compareAt > r2.price)
            ? '<span class="upr-compare">' + fmt(r2.compareAt) + '</span>' : '';
          html += '<div class="unit-price-row' + (k2 === activeUnitIdx ? ' unit-price-row--active' : '') + '" data-unit-idx="' + k2 + '">'
                + '<span class="upr-label">U' + (k2 + 1) + ': ' + tLbl + ' · ' + ui.config.ancho + 'cm · ' + ui.config.niveles + 'niv · ' + bLbl + '</span>'
                + '<span class="upr-value">' + val + cmp + '</span>'
                + (units.length > 1 ? '<button class="upr-remove" type="button" data-remove-unit="' + k2 + '" title="' + t('remove_unit') + '">×</button>' : '')
                + '</div>';
        }
        if (bundleQty > 1 && anyPrice) {
          html += '<div class="unit-price-row upr-sub"><span class="upr-label">' + t('bundle_subtotal') + '</span><span class="upr-value">' + fmt(bundleCents) + '</span></div>';
          html += '<div class="unit-price-row upr-mult"><span class="upr-label">× ' + bundleQty + ' ' + t('bundle_word') + '</span><span class="upr-value"></span></div>';
        }
        if (anyPrice) html += '<div class="unit-price-total"><span>' + fmt(grandCents) + '</span></div>';
      } else {
        var ri0 = results[0];
        if (ri0 && ri0.variantId) {
          var hasSale = !!(ri0.compareAt && ri0.compareAt > ri0.price);
          if (bundleQty > 1) {
            html += '<div class="rc-bundle-single">' + fmt(ri0.price) + '</div>'
                  + '<div class="unit-price-row upr-mult"><span class="upr-label">× ' + bundleQty + ' ' + t('bundle_word') + '</span><span class="upr-value"></span></div>'
                  + '<div class="unit-price-total"><span>' + fmt(grandCents) + '</span></div>';
          } else if (hasSale) {
            var discountPct = Math.round((1 - ri0.price / ri0.compareAt) * 100);
            html += '<span class="rc-sale-price">' + fmt(ri0.price) + '</span>'
                  + '<span class="rc-compare-price">' + fmt(ri0.compareAt) + '</span>'
                  + '<div class="rc-discount-badge">-' + discountPct + '% · ' + fmt(ri0.compareAt - ri0.price) + '</div>';
          } else {
            html += fmt(ri0.price);
          }
        }
      }

      if (priceEl) priceEl.innerHTML = html;
      if (cartBtn) {
        var canBuy = allAvail && !anyUnresolved && anyPrice;
        cartBtn.disabled    = !canBuy;
        // Solo se afirma "Sin stock" cuando SABEMOS que un producto existe y está agotado.
        // Si algo no resolvió, el botón queda deshabilitado con el texto neutro.
        cartBtn.textContent = (!allAvail && !anyUnresolved) ? t('no_stock') : t('add_to_cart');
        cartBtn.className   = 'add-to-cart-btn';
      }
      // Badge de stock bajo el precio: verde "En stock" si disponible, rojo "Sin stock" si no.
      // El rojo SOLO si consta agotado de verdad; si no resolvimos el producto se deja el
      // estado anterior intacto (antes se pintaba rojo y contradecía al propio botón).
      var _dot = document.getElementById('del-stock-dot');
      var _stxt = document.getElementById('del-stock-text');
      if (_dot && _stxt && !anyUnresolved) {
        var _ok = allAvail && anyPrice;
        _dot.style.background = _ok ? '#3b6d11' : '#b42020';
        if (!_stxt.dataset.instock) _stxt.dataset.instock = _stxt.textContent;
        _stxt.textContent = _ok ? _stxt.dataset.instock : t('no_stock');
      }
      bindRowRemoves();
      updateVatNote();
      if (window._configSyncSticky) setTimeout(window._configSyncSticky, 50);
      syncModulesInput();
      updateOptionAvailability();
      // Título + descripción reflejan la unidad activa (single y multi: results[] ya está poblado)
      renderProductContent(results[activeUnitIdx]);
    }
    syncModulesInput();
  }

  // ─── Bind UI selectors ────────────────────────────────────────────────────────
  function bindSelectors() {
    pushHistory();

    // Un botón grisado no debe poder pulsarse. Se hace con UN interceptor en fase de captura
    // en vez de repetir la comprobación en los seis manejadores: así cubre cualquier origen
    // del grisado (huecos deterministas, metacampo, o disponibilidad por red) y no se puede
    // olvidar al añadir un eje nuevo. El valor ACTIVO nunca se bloquea (`apply` ya lo respeta).
    var raiz = document.getElementById('pallet-configurator') || document;
    raiz.addEventListener('click', function (ev) {
      var b = ev.target && ev.target.closest && ev.target.closest('.opt-unavailable');
      if (!b || !raiz.contains(b)) return;
      ev.preventDefault();
      ev.stopPropagation();
    }, true);

    document.querySelectorAll('[data-alto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = btn.dataset.alto; var u = activeUnit();
        if (val === u.config.alto) return;
        u.config.alto = val;
        // Auto-ajustar niveles si no es válido para la nueva altura. Se elige el primer nivel
        // válido que además EXISTA en el catálogo: con 500 kg + 1 módulo, 400×185 con 2 niveles
        // es un hueco pero con 3 no, y quedarse con validN[0] llevaba a una combinación sin
        // precio (y el grisado bloqueaba el botón de 400 por el mismo motivo).
        var validN = ALTURA_NIVELES[String(val)] || [3, 4, 5, 6];
        if (validN.indexOf(u.config.niveles) < 0) u.config.niveles = nivelExistente(u.config, validN);
        normalizeUnitTypes();   // un adicional no puede quedar más alto que su vecino izq.
        setActive('[data-alto]', btn); pushHistory();
        syncPanelToUnit();
        reloadActiveUnit(); renderUnitTabs();
      });
    });

    document.querySelectorAll('[data-ancho]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = btn.dataset.ancho; var u = activeUnit();
        if (val === u.config.ancho) return;
        u.config.ancho = val; setActive('[data-ancho]', btn); pushHistory();
        reloadActiveUnit(); renderUnitTabs();
      });
    });

    document.querySelectorAll('[data-fondo]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = btn.dataset.fondo;
        if (val === u.config.fondo) return;
        // Restricción de gama: el fondo es común a todos los módulos del tramo
        units.forEach(function (un) { un.config.fondo = val; });
        setActive('[data-fondo]', btn); pushHistory();
        refreshUnitsNoCamera();
        try { adjustCameraToAllUnits(_cameraUserMoved); } catch(e) {}
      });
    });

    document.querySelectorAll('[data-niveles]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = parseInt(btn.dataset.niveles, 10);
        if (val === u.config.niveles) return;
        // Auto-ajustar altura si el nivel seleccionado no pertenece a la altura actual
        var curValid = ALTURA_NIVELES[String(u.config.alto)] || [3, 4, 5, 6];
        var altoChanged = false;
        if (curValid.indexOf(val) < 0) {
          var newAlto = null;
          ['200', '250', '300'].forEach(function(hKey) {
            if (!newAlto && (ALTURA_NIVELES[hKey] || []).indexOf(val) >= 0) newAlto = hKey;
          });
          if (newAlto) {
            u.config.alto = newAlto;
            altoChanged = true;
            document.querySelectorAll('[data-alto]').forEach(function(b) {
              b.classList.toggle('active', b.dataset.alto === newAlto);
            });
          }
        }
        u.config.niveles = val;
        if (altoChanged) normalizeUnitTypes();   // si cambió la altura, revalidar adicionales
        setActive('[data-niveles]', btn); pushHistory();
        syncPanelToUnit();
        if (altoChanged) { reloadActiveUnit(); renderUnitTabs(); return; }
        refreshUnitsNoCamera();
      });
    });

    // ── Balda de malla ── añade/quita geometría, así que hay que reensamblar
    document.querySelectorAll('[data-malla]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = (btn.dataset.malla === 'si');
        if (val === !!u.config.malla) return;
        u.config.malla = val; setActive('[data-malla]', btn); pushHistory();
        refreshUnitsNoCamera();
        updatePriceAndCart(); updateSpecs(); renderUnitTabs();
      });
    });

    // ── Protector de puntal ── también es geometría
    document.querySelectorAll('[data-protector]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = (btn.dataset.protector === 'si');
        if (val === !!u.config.protector) return;
        u.config.protector = val; setActive('[data-protector]', btn); pushHistory();
        refreshUnitsNoCamera();
        updatePriceAndCart(); updateSpecs(); renderUnitTabs();
      });
    });

    // ── Carga por palé ── solo cambia de producto (familia del SKU), no la geometría
    document.querySelectorAll('[data-carga]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = String(btn.dataset.carga);
        if (val === String(u.config.carga)) return;
        u.config.carga = val; setActive('[data-carga]', btn); pushHistory();
        updatePriceAndCart(); updateSpecs(); renderUnitTabs();
      });
    });

    // ── Nº de módulos del pack ── cambia el nº de huecos dibujados y el encuadre
    document.querySelectorAll('[data-modulos]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var u = activeUnit(); var val = parseInt(btn.dataset.modulos, 10);
        if (!val || val === parseInt(u.config.modulos, 10)) return;
        // El adicional solo se vende suelto (familias 2 y 4: solo pack de 1 en el catálogo).
        if (val > 1 && unitTypeFor(activeUnitIdx) === 'adicional') {
          showTipoModuloNote(t('adicional_un_modulo'));
          return;
        }
        u.config.modulos = val; setActive('[data-modulos]', btn); pushHistory();
        // Cambia el ancho total de la unidad → hay que recolocar y reencuadrar,
        // no basta con reensamblar en el sitio.
        reloadActiveUnit(); renderUnitTabs();
        updatePriceAndCart(); updateSpecs();
      });
    });

    bindTipoModulo();
    bindQuantity();
    bindModules();
    updatePriceAndCart(); updateSpecs();
  }

  // El selector Inicial/Adicional fija el tipo de la UNIDAD ACTIVA. Visible siempre
  // (en variantes iniciales y adicionales). Las nuevas unidades heredan el tipo de
  // la unidad activa en el momento de añadirlas (ver addUnit).
  function bindTipoModulo() {
    var row = document.getElementById('row-tipo-modulo');
    if (row) row.style.display = '';
    document.querySelectorAll('[data-tipo-modulo]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var val = btn.dataset.tipoModulo;        // 'inicial' | 'adicional'
        var u = activeUnit();
        if (u.tipoModulo === val) return;
        // No permitir adicional más alto que el módulo a su izquierda (comparte su bastidor).
        if (val === 'adicional' && !canBeAdicional(activeUnitIdx)) {
          showTipoModuloNote(t('adicional_invalid'));
          return;
        }
        u.tipoModulo = val;
        // El módulo ADICIONAL solo se vende suelto: en el catálogo las familias 2 y 4 existen
        // ÚNICAMENTE con pack de 1 (medido: 64 y 52 productos, todos `01`). Dejar 2-4 módulos
        // seleccionados al pasar a adicional lleva a una combinación que no existe, y el
        // usuario ve el precio vacío sin entender por qué. Se baja a 1 y se avisa.
        if (val === 'adicional' && (parseInt(u.config.modulos, 10) || 1) > 1) {
          u.config.modulos = 1;
          setActive('[data-modulos]', document.querySelector('[data-modulos="1"]'));
          var lm = document.getElementById('label-modulos');
          if (lm) lm.textContent = '1';
          showTipoModuloNote(t('adicional_un_modulo'));
        }
        aplicaLimiteModulos(val);
        setActive('[data-tipo-modulo]', btn);
        var labelEl = document.getElementById('label-tipo-modulo');
        if (labelEl) labelEl.textContent = btn.textContent.trim();
        pushHistory();
        refreshUnitsNoCamera();                  // rebuild (geometría + precio)
        try { adjustCameraToAllUnits(_cameraUserMoved); } catch(e) {}
        renderUnitTabs(); updateSpecs();
      });
    });
  }

  // Con módulo ADICIONAL solo existe el pack de 1 (ver el comentario del click handler), así
  // que los botones 2-4 se bloquean en vez de dejar caer al usuario en una combinación
  // inexistente. Se usa la misma clase que el resto del grisado para que se vea igual.
  function aplicaLimiteModulos(tipo) {
    var soloUno = (tipo || 'inicial') === 'adicional';
    document.querySelectorAll('[data-modulos]').forEach(function (b) {
      var n = parseInt(b.dataset.modulos, 10) || 1;
      var bloqueado = soloUno && n > 1;
      b.classList.toggle('opt-unavailable', bloqueado);
      if (bloqueado) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
    });
  }

  // Aviso transitorio bajo el selector de tipo de módulo.
  function showTipoModuloNote(msg) {
    var row = document.getElementById('row-tipo-modulo');
    if (!row) return;
    var note = document.getElementById('tipo-modulo-note');
    if (!note) {
      note = document.createElement('div');
      note.id = 'tipo-modulo-note';
      note.className = 'rc-tipo-note';
      note.setAttribute('role', 'status');
      row.appendChild(note);
    }
    note.textContent = msg;
    note.classList.add('rc-tipo-note--show');
    if (showTipoModuloNote._t) clearTimeout(showTipoModuloNote._t);
    showTipoModuloNote._t = setTimeout(function () {
      note.classList.remove('rc-tipo-note--show');
    }, 4000);
  }

  // ─── Selector de cantidad = nº de CONJUNTOS (bundles) ─────────────────────────
  // El run completo (todas las unidades) se replica bundleQty veces como pasillos
  // paralelos en Z. NO añade unidades al array; multiplica el conjunto al carrito.

  // Dos unidades son "la misma config" si comparten los 8 ejes: es lo que decide si se
  // agrupan en una sola línea de carrito.
  function configsMatch(a, b) {
    return String(a.alto)      === String(b.alto)      &&
           String(a.ancho)     === String(b.ancho)     &&
           String(a.fondo)     === String(b.fondo)     &&
           String(a.niveles)   === String(b.niveles)   &&
           String(a.modulos)   === String(b.modulos)   &&
           String(a.carga)     === String(b.carga)     &&
           (!!a.malla)         === (!!b.malla)         &&
           (!!a.protector)     === (!!b.protector);
  }

  function countActiveConfigUnits() {
    var cfg = activeUnit().config;
    var n = 0;
    for (var i = 0; i < units.length; i++) {
      if (configsMatch(units[i].config, cfg)) n++;
    }
    return n;
  }

  function removeOneOfActiveConfig() {
    if (countActiveConfigUnits() <= 1) return;
    var cfg = activeUnit().config;
    for (var i = units.length - 1; i >= 0; i--) {
      if (i !== activeUnitIdx && configsMatch(units[i].config, cfg)) { removeUnit(i); return; }
    }
  }

  function syncModulesInput() {
    var count = countActiveConfigUnits();
    var input = document.getElementById('modules-value');
    if (input) input.value = count;
    var minus = document.getElementById('modules-minus');
    if (minus) minus.disabled = count <= 1;
  }

  function bindModules() {
    var minus = document.getElementById('modules-minus');
    var plus  = document.getElementById('modules-plus');
    var input = document.getElementById('modules-value');
    if (plus)  plus.addEventListener('click', function() { addUnit(); });
    if (minus) minus.addEventListener('click', function() { removeOneOfActiveConfig(); });
    if (input) input.addEventListener('change', function() {
      var target  = Math.max(1, Math.min(MAX_UNITS, parseInt(input.value, 10) || 1));
      var current = countActiveConfigUnits();
      if (target > current) { for (var a = current; a < target; a++) addUnit(); }
      else                  { for (var b = target; b < current; b++) removeOneOfActiveConfig(); }
      syncModulesInput();
    });
    syncModulesInput();
  }

  function syncQtyInput() {
    var input = document.getElementById('qty-value');
    if (input) input.value = bundleQty;
    var minus = document.getElementById('qty-minus');
    if (minus) minus.disabled = bundleQty <= 1;
    var plus = document.getElementById('qty-plus');
    if (plus) plus.disabled = bundleQty >= MAX_BUNDLES;
  }

  function setBundleQty(target) {
    if (isNaN(target) || target < 1) target = 1;
    if (target > MAX_BUNDLES) target = MAX_BUNDLES;
    if (target === bundleQty) { syncQtyInput(); return; }
    bundleQty = target;
    pushHistory();
    buildBundleClones();
    adjustCameraToAllUnits(_cameraUserMoved);
    syncQtyInput();
    updatePriceAndCart();
  }

  function bindQuantity() {
    var input = document.getElementById('qty-value');
    var minus = document.getElementById('qty-minus');
    var plus  = document.getElementById('qty-plus');
    if (plus)  plus.addEventListener('click', function () { setBundleQty(bundleQty + 1); });
    if (minus) minus.addEventListener('click', function () { setBundleQty(bundleQty - 1); });
    if (input) input.addEventListener('change', function () {
      setBundleQty(parseInt(input.value, 10));
    });
    syncQtyInput();
  }

  function setActive(selector, activeBtn) {
    document.querySelectorAll(selector).forEach(function (b) { b.classList.remove('active'); });
    activeBtn.classList.add('active');
  }

  /** Añade UN grupo (variantId × qty) al carrito. Llama onDone() al terminar. */
  function addToCart(variantId, qty, onDone) {
    qty = qty || 1;
    if (!window.Shopify) {
      console.log('[PAL] Dev mode — Variant ID:', variantId, '×', qty);
      if (onDone) onDone(true);
      return;
    }
    fetch(shopRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || ('Error ' + r.status)); });
        return r.json();
      })
      .then(function (item) {
        document.dispatchEvent(new CustomEvent('cart:add', { detail: item, bubbles: true }));
        if (onDone) onDone(true);
      })
      .catch(function (err) {
        console.error('[PAL] Cart error:', err);
        if (onDone) onDone(false);
      });
  }

  /** Añade al carrito todos los grupos (inicial + adicionales agrupados). */
  function addAllToCart() {
    var btn = document.getElementById('add-to-cart-btn'); if (!btn) return;

    if (window.Shopify && window.Shopify.designMode) {
      btn.textContent = t('preview_mode');
      btn.className = 'add-to-cart-btn';
      setTimeout(function () { btn.textContent = t('add_to_cart'); btn.disabled = false; }, 3000);
      return;
    }

    btn.disabled = true; btn.textContent = t('adding'); btn.className = 'add-to-cart-btn';

    var groups = buildCartGroups();
    var idx = 0;
    var allOk = true, stockIssue = false;

    function next(ok) {
      if (!ok) allOk = false;
      if (idx >= groups.length) {
        if (allOk) {
          btn.textContent = t('added'); btn.className = 'add-to-cart-btn success';
          if (window.Theme && window.Theme.cart) {
            window.Theme.cart.fetch().then(function () {
              if (window.Theme.settings && window.Theme.settings.cart_type === 'drawer' && window.Theme.openCart) {
                window.Theme.openCart();
              } else if (window.Theme.toast) {
                window.Theme.toast(t('added'));
              }
            });
          } else if (window.Shopify) {
            fetch(shopRoot() + 'cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                document.querySelectorAll('[data-cart-count], .cart-bubble__text-count').forEach(function (el) {
                  el.textContent = cart.item_count;
                  el.style.display = cart.item_count > 0 ? '' : 'none';
                });
                document.querySelectorAll('.cart-bubble').forEach(function (el) {
                  el.classList.remove('cart-bubble--hidden');
                });
                var drawer = document.querySelector('#CartDrawer, cart-drawer');
                if (window.Theme && window.Theme.openCart) {
                  window.Theme.openCart();
                } else if (drawer && typeof drawer.open === 'function') {
                  drawer.open();
                }
              });
          } else {
            btn.textContent = '✓ (Dev mode)'; btn.className = 'add-to-cart-btn success';
          }
        } else {
          var failMsg = stockIssue ? t('no_stock') : t('cart_error');
          btn.textContent = failMsg; btn.className = 'add-to-cart-btn error';
          if (window.Theme && window.Theme.toast) window.Theme.toast(failMsg, 'error');
        }
        setTimeout(function () { btn.textContent = t('add_to_cart'); btn.className = 'add-to-cart-btn'; btn.disabled = false; }, 2500);
        return;
      }
      var g = groups[idx++];
      getCachedOrFetch(g.config, g.unitType, function (info) {
        if (info && info.variantId && info.available !== false) {
          addToCart(info.variantId, g.count, next);
        } else {
          if (info && info.available === false) stockIssue = true;   // agotado: no intentar el POST (evita el 422)
          console.warn('[PAL] Producto no disponible para añadir al carrito:', g.unitType, g.config, info);
          next(false);
        }
      }, true);
    }
    next(true);
  }

  // ─── Actualizar thumbs de galería con imágenes del producto ─────────────────
  function updateGalleryThumbs(images) {
    var track = document.querySelector("[data-rc-gallery-thumbs]");
    if (!track) return;
    if (!images || !images.length) return;

    var children = Array.prototype.slice.call(track.children);
    children.forEach(function(el, i) { if (i > 0) el.remove(); });

    images.slice(0, 8).forEach(function(src) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "rc-thumb";
      btn.setAttribute("data-rc-gallery-thumb", "");
      btn.dataset.src = src;
      var img = document.createElement("img");
      img.src = src; img.alt = ""; img.loading = "lazy"; img.decoding = "async";
      btn.appendChild(img);
      track.appendChild(btn);
      btn.addEventListener("click", function() {
        var viewer3d = document.getElementById("viewer-3d-wrap");
        var photo = document.getElementById("gallery-photo");
        var thumb3d = document.querySelector("[data-rc-thumb-3d]");
        if (viewer3d) viewer3d.style.display = "none";
        if (photo) { photo.src = src; photo.classList.remove("hidden"); }
        if (thumb3d) thumb3d.classList.remove("active");
        document.querySelectorAll("[data-rc-gallery-thumb]").forEach(function(t){ t.classList.remove("active"); });
        btn.classList.add("active");
      });
    });

    var wrap = document.querySelector("[data-rc-thumbs-wrap]");
    if (wrap) wrap.style.display = "";
  }

  // ─── Galería de imágenes + toggle 3D / foto ──────────────────────────────────
  function initGallery() {
    var thumb3d    = document.querySelector('[data-rc-thumb-3d]');
    var imgThumbs  = document.querySelectorAll('[data-rc-gallery-thumb]');
    var viewer3d   = document.getElementById('viewer-3d-wrap');
    var galleryPhoto = document.getElementById('gallery-photo');
    var track      = document.querySelector('[data-rc-gallery-thumbs]');
    var prevBtn    = document.querySelector('[data-rc-thumbs-prev]');
    var nextBtn    = document.querySelector('[data-rc-thumbs-next]');

    if (!track) return;

    function setActive3D() {
      if (viewer3d)     viewer3d.style.display = '';
      if (galleryPhoto) galleryPhoto.classList.add('hidden');
      if (thumb3d) thumb3d.classList.add('active');
      imgThumbs.forEach(function(t) { t.classList.remove('active'); });
    }

    function setActivePhoto(src) {
      if (viewer3d) viewer3d.style.display = 'none';
      if (galleryPhoto) {
        galleryPhoto.src = src;
        galleryPhoto.classList.remove('hidden');
      }
      if (thumb3d) thumb3d.classList.remove('active');
    }

    if (thumb3d) {
      thumb3d.addEventListener('click', function() {
        setActive3D();
      });
    }

    imgThumbs.forEach(function(tb) {
      tb.addEventListener('click', function() {
        imgThumbs.forEach(function(x) { x.classList.remove('active'); });
        tb.classList.add('active');
        setActivePhoto(tb.dataset.src);
      });
    });

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var hasOverflow = track.scrollWidth - track.clientWidth > 1;
      if (!hasOverflow) { prevBtn.hidden = true; nextBtn.hidden = true; return; }
      prevBtn.hidden = track.scrollLeft <= 1;
      nextBtn.hidden = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }
    if (prevBtn) prevBtn.addEventListener('click', function() {
      track.scrollBy({ left: -track.clientWidth * 0.75, behavior: 'smooth' });
    });
    if (nextBtn) nextBtn.addEventListener('click', function() {
      track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
    });
    if (track) { track.addEventListener('scroll', updateArrows); window.addEventListener('resize', updateArrows); }
    setTimeout(updateArrows, 200);
  }

  // ─── Botón de consulta — mailto con configuración pre-rellena ────────────────
  function initConsultationButton() {
    var data = window.PalConfiguratorData || {};
    var email = data.consultationEmail || 'hola@misterregale.com';

    // Frases del email por idioma (las etiquetas de campos van por t('pdf_*'), ya
    // localizadas). Se elige por Shopify.locale; el correo sale en el idioma del
    // mercado sin tocar los archivos de locale.
    var MAIL_TXT = {
      es: { subject:'Consulta de configuración', greeting:'Hola', intro:'Estoy interesado en la siguiente configuración:', link:'Enlace de configuración:', thanks:'Gracias', full:'Configuración completa ({n} módulos):', module:'Módulo' },
      de: { subject:'Konfigurationsanfrage', greeting:'Hallo', intro:'Ich interessiere mich für die folgende Konfiguration:', link:'Konfigurationslink:', thanks:'Vielen Dank', full:'Vollständige Konfiguration ({n} Module):', module:'Modul' },
      en: { subject:'Configuration enquiry', greeting:'Hello', intro:'I am interested in the following configuration:', link:'Configuration link:', thanks:'Thank you', full:'Full configuration ({n} modules):', module:'Module' },
      cs: { subject:'Dotaz ke konfiguraci', greeting:'Dobrý den', intro:'Mám zájem o následující konfiguraci:', link:'Odkaz na konfiguraci:', thanks:'Děkuji', full:'Kompletní konfigurace ({n} modulů):', module:'Modul' },
      sk: { subject:'Dopyt ku konfigurácii', greeting:'Dobrý deň', intro:'Mám záujem o nasledujúcu konfiguráciu:', link:'Odkaz na konfiguráciu:', thanks:'Ďakujem', full:'Kompletná konfigurácia ({n} modulov):', module:'Modul' },
      hu: { subject:'Konfigurációs érdeklődés', greeting:'Jó napot kívánok', intro:'Érdeklődöm a következő konfiguráció iránt:', link:'Konfigurációs link:', thanks:'Köszönöm', full:'Teljes konfiguráció ({n} modul):', module:'Modul' },
      pt: { subject:'Consulta de configuração', greeting:'Olá', intro:'Tenho interesse na seguinte configuração:', link:'Link de configuração:', thanks:'Obrigado', full:'Configuração completa ({n} módulos):', module:'Módulo' }
    };
    function mailTxt() {
      var l = (window.Shopify && Shopify.locale ? String(Shopify.locale).slice(0, 2) : 'es').toLowerCase();
      return MAIL_TXT[l] || MAIL_TXT.es;
    }

    function buildMailto() {
      var cfg = activeUnit().config;
      var tipoLbl   = baldaDisplay(cfg.malla);
      var nivWord   = t('niveles_word') || 'niveles';
      var M = mailTxt();
      var name = t('summary_prefix') || data.productTitle || 'Estantería para palés';
      var subject = M.subject + ' — ' + name + ' ' + cfg.alto + '×' + cfg.ancho + '×' + cfg.fondo + ' cm';
      var body = M.greeting + ',\n\n' + M.intro + '\n\n'
               + '  • ' + t('pdf_altura') + ': ' + cfg.alto + ' cm\n'
               + '  • ' + t('pdf_anchura') + ': ' + cfg.ancho + ' cm\n'
               + '  • ' + t('pdf_fondo') + ': ' + cfg.fondo + ' cm\n'
               + '  • ' + t('pdf_niveles') + ': ' + cfg.niveles + '\n'
               + '  • ' + t('pdf_tipo_balda') + ': ' + tipoLbl + '\n';

      if (units.length > 1) {
        body += '\n' + M.full.replace('{n}', units.length) + '\n';
        units.forEach(function(u, i) {
          var tLbl = unitTypeFor(i) === 'inicial' ? t('pdf_mod_inicial') : t('pdf_mod_adicional');
          body += '  ' + M.module + ' ' + (i+1) + ' (' + tLbl + '): ' + u.config.ancho + 'cm · ' + u.config.niveles + ' ' + nivWord + ' · ' + baldaDisplay(u.config.malla) + '\n';
        });
      }

      body += '\n' + M.link + ' ' + buildShareUrl();
      body += '\n\n' + M.thanks;

      return 'mailto:' + email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    }

    function updateConsultationHref() {
      var btn = document.getElementById('consultation-link');
      if (btn) btn.href = buildMailto();
      document.querySelectorAll('.consultation-btn').forEach(function(b) {
        if (!b.id || b.id !== 'consultation-link') {
          b.addEventListener('click', function(e) {
            e.preventDefault();
            window.location.href = buildMailto();
          });
        }
      });
    }

    updateConsultationHref();
    window._updateConsultationHref = updateConsultationHref;
  }

  // ─── Ficha técnica compacta dentro del visor ─────────────────────────────
  function initTechCard() {
    var wrap = document.getElementById("viewer-3d-wrap");
    if (!wrap) return;

    var card = document.createElement("div");
    card.id = "viewer-tech-card";
    card.className = "viewer-tech-card";
    card.innerHTML =
      '<button class="vtc-toggle" id="vtc-toggle" title="' + t('spec_sheet_short') + '">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
      '</button>' +
      '<div class="vtc-body" id="vtc-body">' +
      '<div class="vtc-row"><span class="vtc-lbl" id="vtc-height-lbl">' + t('pdf_altura') + '</span><span class="vtc-val" id="vtc-height">—</span></div>' +
      '<div class="vtc-row"><span class="vtc-lbl" id="vtc-width-lbl">' + t('pdf_anchura') + '</span><span class="vtc-val" id="vtc-width">—</span></div>' +
      '<div class="vtc-row"><span class="vtc-lbl" id="vtc-depth-lbl">' + t('pdf_fondo') + '</span><span class="vtc-val" id="vtc-depth">—</span></div>' +
      '<div class="vtc-row"><span class="vtc-lbl">' + t('pdf_niveles') + '</span><span class="vtc-val" id="vtc-levels">—</span></div>' +
      '<div class="vtc-row"><span class="vtc-lbl">' + String(t('pdf_carga') || 'Carga').split('/')[0].trim() + '</span><span class="vtc-val" id="vtc-capacity">—</span></div>' +
      '</div>';

    var open = true;
    var toggle = card.querySelector("#vtc-toggle");
    var body = card.querySelector("#vtc-body");
    if (toggle) toggle.addEventListener("click", function() {
      open = !open;
      body.style.display = open ? "" : "none";
      toggle.classList.toggle("vtc-collapsed", !open);
    });

    wrap.appendChild(card);
    updateTechCard();
  }

  function updateTechCard() {
    var cfg = activeUnit().config;
    var H = getFrameHeight(cfg);
    var W = (PRODUCT_DIMS.widths[cfg.ancho] || 0) * (parseInt(cfg.modulos, 10) || 1);
    var D = PRODUCT_DIMS.depths[cfg.fondo] || 1100;
    var cap = cargaPorPale(cfg);
    function s(id, v) { var el = document.getElementById(id); if (el) el.textContent = v; }
    s("vtc-height", H + " mm");
    s("vtc-width",  W + " mm");
    s("vtc-depth",  D + " mm");
    s("vtc-levels", cfg.niveles);
    s("vtc-capacity", cap + " kg");
    s("vtc-pales", palesTotales(cfg));
  }

  // ─── Sticky CTA mobile ────────────────────────────────────────────────────────
  function initStickyMobileCta() {
    var realBtn      = document.getElementById('add-to-cart-btn');
    var stickyBar    = document.getElementById('config-sticky-cta');
    var stickyBtn    = document.getElementById('sticky-add-to-cart');
    var stickyPriceEl = document.getElementById('sticky-price-display');
    if (!realBtn || !stickyBar) return;

    function syncSticky() {
      var priceEl = document.getElementById('variant-price');
      if (priceEl && stickyPriceEl) {
        var totalEl = priceEl.querySelector('.unit-price-total');
        if (totalEl) {
          var totalSpan = totalEl.querySelector('span');
          stickyPriceEl.innerHTML = totalSpan ? totalSpan.innerHTML : totalEl.innerHTML;
        } else {
          stickyPriceEl.innerHTML = priceEl.innerHTML;
        }
      }
      if (stickyBtn && realBtn) {
        stickyBtn.disabled = realBtn.disabled;
      }
    }

    if (stickyBtn) stickyBtn.addEventListener('click', addAllToCart);

    if (window.IntersectionObserver) {
      var obs = new IntersectionObserver(function(entries) {
        var visible = entries[0].isIntersecting;
        stickyBar.classList.toggle('hidden', visible);
        stickyBar.setAttribute('aria-hidden', String(visible));
        if (!visible) syncSticky();
      }, { threshold: 0.1 });
      obs.observe(realBtn);
    }

    window._configSyncSticky = syncSticky;
  }

  // Visor sticky de altura ESTABLE en móvil.
  // ANTES: encogía al hacer scroll (rc-gallery--compact, 50dvh→30dvh). Pero
  // cambiar la altura de un elemento sticky superior desplaza el documento
  // ~140px en mitad del scroll: al subir hacia el top el visor volvía a crecer
  // y empujaba el contenido hacia abajo, "peleando" con el scroll (parecía que
  // no se podía llegar arriba). Se desactiva el encogido — el visor mantiene su
  // altura fija. El reajuste del canvas en resize ya se gestiona en init().
  function initStickyViewerShrink() { /* desactivado a propósito (ver nota arriba) */ }

  function waitForLibraries(cb, elapsed) {
    elapsed = elapsed || 0;
    if (window.THREE && window.THREE.OrbitControls && window.THREE.GLTFLoader) { cb(); return; }
    if (elapsed >= 10000) {
      console.error('[PAL] Three.js no se cargó tras 10s.');
      var errEl = document.getElementById('model-error');
      if (errEl) errEl.classList.remove('hidden');
      var loadEl = document.getElementById('loading-overlay');
      if (loadEl) loadEl.classList.add('hidden');
      return;
    }
    setTimeout(function () { waitForLibraries(cb, elapsed + 50); }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForLibraries(init); });
  } else {
    waitForLibraries(init);
  }

})();
